// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Master Alien Species Database
 * 
 * Central configuration for all alien species across all universes.
 * Each universe can reference species from this master list.
 * 
 * Species are organized by fictional universe of origin for easier management.
 */

const AlienSpecies = {
  // ============================================================================
  // DOUGLAS ADAMS UNIVERSE (Hitchhiker's Guide to the Galaxy)
  // ============================================================================
  
  vogons: {
    id: 'vogons',
    name: 'Vogons',
    origin: 'Douglas Adams',
    homeworld: 'Vogsphere',
    description: 'Unpleasant, bureaucratic aliens known for their terrible poetry and love of paperwork. Third worst poets in the universe.',
    appearance: {
      ascii: '👽📋',
      color: '#8b7355',
      description: 'Slug-like, green-brown, with multiple chins'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Average',
      technology: 'Advanced',
      culture: 'Bureaucratic',
      aggression: 'High'
    },
    dialogue: [
      'Resistance is useless!',
      'You are in violation of form 27B/6.',
      'Prepare for poetry reading.',
      'All paperwork must be filed in triplicate.',
      'Your planet is scheduled for demolition.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common'
  },

  babelfish: {
    id: 'babelfish',
    name: 'Babel Fish',
    origin: 'Douglas Adams',
    homeworld: 'Unknown',
    description: 'Small, yellow, leech-like fish that feeds on brainwave energy. When inserted into the ear, it provides instant translation of any language.',
    appearance: {
      ascii: '🐟💭',
      color: '#ffff00',
      description: 'Small, yellow, leech-like'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Biological',
      culture: 'Symbiotic',
      aggression: 'None'
    },
    dialogue: [
      '*translates everything perfectly*',
      '*feeds on brainwave energy*',
      '*universal understanding achieved*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 500
  },

  magratheans: {
    id: 'magratheans',
    name: 'Magratheans',
    origin: 'Douglas Adams',
    homeworld: 'Magrathea',
    description: 'Ancient planet builders who constructed luxury planets for the ultra-rich. Masters of planetary engineering.',
    appearance: {
      ascii: '🌍🔧',
      color: '#c0c0c0',
      description: 'Tall, elegant, silver-skinned'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Genius',
      technology: 'Godlike',
      culture: 'Artistic',
      aggression: 'None'
    },
    dialogue: [
      'We built planets for a living.',
      'Would you like to purchase a custom world?',
      'Our work is legendary across the galaxy.',
      'Five million years of sleep was quite refreshing.',
      'Earth Mark II is our finest work.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Very Rare',
    tradeValue: 2000
  },

  hooloovoo: {
    id: 'hooloovoo',
    name: 'Hooloovoo',
    origin: 'Douglas Adams',
    homeworld: 'Unknown',
    description: 'A super-intelligent shade of the color blue. Exists as pure color energy.',
    appearance: {
      ascii: '🔵✨',
      color: '#0000ff',
      description: 'Sentient blue light'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Super-Genius',
      technology: 'Energy-based',
      culture: 'Philosophical',
      aggression: 'None'
    },
    dialogue: [
      '*shimmers in contemplation*',
      '*radiates blue wisdom*',
      'I exist as pure color.',
      '*wavelength shifts thoughtfully*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Very Rare'
  },

  mice: {
    id: 'mice',
    name: 'Hyperintelligent Mice',
    origin: 'Douglas Adams',
    homeworld: 'Earth (secretly)',
    description: 'Pan-dimensional beings who appear as mice. Actually the most intelligent species on Earth, running experiments on humans.',
    appearance: {
      ascii: '🐭🧠',
      color: '#808080',
      description: 'Appear as ordinary white mice'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Super-Genius',
      technology: 'Pan-dimensional',
      culture: 'Scientific',
      aggression: 'Low'
    },
    dialogue: [
      'The experiment continues as planned.',
      'Humans are such interesting subjects.',
      'We need to find the Ultimate Question.',
      'Earth was a supercomputer, you know.',
      '*squeaks knowingly*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Rare'
  },

  // ============================================================================
  // STAR TREK UNIVERSE
  // ============================================================================

  vulcans: {
    id: 'vulcans',
    name: 'Vulcans',
    origin: 'Star Trek',
    homeworld: 'Vulcan',
    description: 'Logical, emotionless humanoids with pointed ears. Masters of logic and mental discipline.',
    appearance: {
      ascii: '🖖👤',
      color: '#90ee90',
      description: 'Humanoid with pointed ears, green-tinted skin'
    },
    traits: {
      temperament: 'Logical',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Philosophical',
      aggression: 'Very Low'
    },
    dialogue: [
      'Live long and prosper.',
      'That is illogical.',
      'Fascinating.',
      'The needs of the many outweigh the needs of the few.',
      'I am a Vulcan. I have no ego to bruise.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 300
  },

  klingons: {
    id: 'klingons',
    name: 'Klingons',
    origin: 'Star Trek',
    homeworld: 'Qo\'noS',
    description: 'Warrior race with a strong sense of honor. Value combat prowess and loyalty above all.',
    appearance: {
      ascii: '⚔️👹',
      color: '#8b4513',
      description: 'Humanoid with ridged foreheads, dark skin'
    },
    traits: {
      temperament: 'Aggressive',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Warrior',
      aggression: 'High'
    },
    dialogue: [
      'Today is a good day to die!',
      'Qapla\'! (Success!)',
      'Honor is everything.',
      'Perhaps you are a worthy opponent.',
      'Glory to the Empire!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 400
  },

  ferengi: {
    id: 'ferengi',
    name: 'Ferengi',
    origin: 'Star Trek',
    homeworld: 'Ferenginar',
    description: 'Profit-driven merchants who live by the Rules of Acquisition. Excellent traders with large ears.',
    appearance: {
      ascii: '💰👂',
      color: '#ffa500',
      description: 'Short humanoid with large ears, orange skin'
    },
    traits: {
      temperament: 'Greedy',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Capitalist',
      aggression: 'Low'
    },
    dialogue: [
      'Greed is eternal.',
      'What\'s your offer?',
      'A deal is a deal... until a better one comes along.',
      'Profit is its own reward.',
      'Never place friendship above profit.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 250
  },

  borg: {
    id: 'borg',
    name: 'Borg',
    origin: 'Star Trek',
    homeworld: 'Unknown (Collective)',
    description: 'Cybernetic collective consciousness. Assimilate other species and technology into their hive mind.',
    appearance: {
      ascii: '🤖🔗',
      color: '#00ff00',
      description: 'Cybernetic humanoid with visible implants'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Collective Genius',
      technology: 'Superior',
      culture: 'Hive Mind',
      aggression: 'Very High'
    },
    dialogue: [
      'Resistance is futile.',
      'You will be assimilated.',
      'We are the Borg.',
      'Your biological and technological distinctiveness will be added to our own.',
      'Lower your shields and surrender your ships.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare'
  },

  andorians: {
    id: 'andorians',
    name: 'Andorians',
    origin: 'Star Trek',
    homeworld: 'Andoria',
    description: 'Blue-skinned humanoids with antennae. Proud warrior culture with strong sense of honor.',
    appearance: {
      ascii: '📡💙',
      color: '#4169e1',
      description: 'Blue-skinned humanoid with antennae'
    },
    traits: {
      temperament: 'Proud',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Warrior',
      aggression: 'Medium'
    },
    dialogue: [
      'Andorians do not back down.',
      'Our honor demands action.',
      'The ice forges strength.',
      'We stand with our allies.',
      'Trust must be earned.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 350
  },

  // ============================================================================
  // CLASSIC SCI-FI
  // ============================================================================

  greys: {
    id: 'greys',
    name: 'Grey Aliens',
    origin: 'UFO Lore',
    homeworld: 'Zeta Reticuli (alleged)',
    description: 'Classic grey aliens with large heads and black eyes. Mysterious observers of humanity.',
    appearance: {
      ascii: '👽🛸',
      color: '#808080',
      description: 'Grey skin, large head, black almond eyes'
    },
    traits: {
      temperament: 'Mysterious',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Scientific',
      aggression: 'Low'
    },
    dialogue: [
      '*communicates telepathically*',
      'We have been observing.',
      'Your species shows potential.',
      '*probes curiously*',
      'Do not be afraid.'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Uncommon'
  },

  martians: {
    id: 'martians',
    name: 'Martians',
    origin: 'War of the Worlds',
    homeworld: 'Mars',
    description: 'Tentacled beings from Mars with advanced technology. Seeking new worlds to colonize.',
    appearance: {
      ascii: '🐙🔴',
      color: '#ff0000',
      description: 'Tentacled, bulbous head, no visible features'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'Imperialist',
      aggression: 'Very High'
    },
    dialogue: [
      '*emits haunting tripod sound*',
      'ULLA! ULLA!',
      '*heat ray charges*',
      '*mechanical tentacles extend*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare'
  },

  // ============================================================================
  // POKÉMON UNIVERSE
  // ============================================================================

  pikachu: {
    id: 'pikachu',
    name: 'Pikachu',
    origin: 'Pokémon',
    homeworld: 'Kanto Prime',
    description: 'The Electric Mouse Pokémon. Mascot of the Pokémon world. Stores electricity in its cheeks.',
    appearance: {
      ascii: '⚡🐭',
      color: '#ffff00',
      description: 'Small yellow mouse with red cheeks'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Electric abilities',
      culture: 'Companion',
      aggression: 'Low'
    },
    dialogue: [
      'Pika pika! ⚡',
      'Pika-CHU! *electric shock*',
      'Pikachu! ^_^ *happy dance*',
      '*nuzzles affectionately*',
      '*cheeks spark with electricity*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 2000
  },

  charizard: {
    id: 'charizard',
    name: 'Charizard',
    origin: 'Pokémon',
    homeworld: 'Kanto Prime',
    description: 'The Flame Pokémon. Breathes fire hot enough to melt boulders. Flies in search of powerful opponents.',
    appearance: {
      ascii: '🔥🐉',
      color: '#ff4500',
      description: 'Large orange dragon with flaming tail'
    },
    traits: {
      temperament: 'Proud',
      intelligence: 'High',
      technology: 'Fire abilities',
      culture: 'Warrior',
      aggression: 'Medium'
    },
    dialogue: [
      '*Roars and breathes fire* Show me your strength!',
      'You dare challenge me? Face my flames!',
      '*Nods with respect* You are a worthy trainer.',
      '*Wings spread majestically*',
      '*Flame intensifies*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 7000
  },

  eevee: {
    id: 'eevee',
    name: 'Eevee',
    origin: 'Pokémon',
    homeworld: 'Kanto Prime',
    description: 'The Evolution Pokémon. Has an unstable genetic makeup that can evolve into eight different forms.',
    appearance: {
      ascii: '🦊✨',
      color: '#c19a6b',
      description: 'Small brown fox-like creature'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Evolution abilities',
      culture: 'Adaptive',
      aggression: 'Very Low'
    },
    dialogue: [
      'Eevee! *tilts head curiously*',
      'Vee vee! *defensive stance*',
      'Eevee! *nuzzles affectionately*',
      '*tail wags happily*',
      '*eyes sparkle with potential*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 1500
  },

  lucario: {
    id: 'lucario',
    name: 'Lucario',
    origin: 'Pokémon',
    homeworld: 'Sinnoh Highlands',
    description: 'The Aura Pokémon. Can sense and manipulate aura. Reads opponents\' thoughts and movements.',
    appearance: {
      ascii: '🔵🥋',
      color: '#4169e1',
      description: 'Blue and black canine warrior'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Very High',
      technology: 'Aura manipulation',
      culture: 'Warrior',
      aggression: 'Medium'
    },
    dialogue: [
      'I sense your aura. Your intentions are... interesting.',
      'Your aura reveals hostility. I will defend myself!',
      'Your aura is pure. You have my respect, warrior.',
      '*aura intensifies*',
      '*reads your emotions*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 6000
  },

  greninja: {
    id: 'greninja',
    name: 'Greninja',
    origin: 'Pokémon',
    homeworld: 'Kalos Nebula',
    description: 'The Ninja Pokémon. Moves with the speed and grace of a ninja. Creates throwing stars from compressed water.',
    appearance: {
      ascii: '💧🥷',
      color: '#4682b4',
      description: 'Dark blue ninja frog'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Very High',
      technology: 'Ninja techniques',
      culture: 'Stealth',
      aggression: 'Medium'
    },
    dialogue: [
      '*Appears from shadows* ...You have potential.',
      '*Vanishes and reappears* Your movements are too slow.',
      '*Nods silently* You fight with honor.',
      '*creates water shuriken*',
      '*disappears in mist*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 6000
  },

  mewtwo: {
    id: 'mewtwo',
    name: 'Mewtwo',
    origin: 'Pokémon',
    homeworld: 'Kanto Prime',
    description: 'A genetically engineered Psychic-type Pokémon. Incredibly powerful and intelligent.',
    appearance: {
      ascii: '🧬👁️',
      color: '#9370db',
      description: 'Humanoid psychic entity with purple aura'
    },
    traits: {
      temperament: 'Aggressive',
      intelligence: 'Genius',
      technology: 'Psychic powers',
      culture: 'Solitary',
      aggression: 'High'
    },
    dialogue: [
      'I am Mewtwo. I was created to be the strongest. Prove your worth.',
      'Your presence disturbs my meditation. Leave, or face my power!',
      'You have earned my respect. Perhaps humans are not all the same.',
      '*psychic energy radiates*',
      '*eyes glow with power*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 10000
  },

  rayquaza: {
    id: 'rayquaza',
    name: 'Rayquaza',
    origin: 'Pokémon',
    homeworld: 'Hoenn Archipelago',
    description: 'The Sky High Pokémon. Guardian of the atmosphere, mediator of Groudon and Kyogre.',
    appearance: {
      ascii: '🐉☁️',
      color: '#228b22',
      description: 'Massive green serpentine dragon'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Very High',
      technology: 'Weather control',
      culture: 'Guardian',
      aggression: 'Medium'
    },
    dialogue: [
      'I am the guardian of these skies. State your purpose.',
      'You threaten the balance. I will not allow it!',
      'The skies are calm with you here. Fly safely, traveler.',
      '*soars through atmosphere*',
      '*roar echoes across the sky*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 10000
  },

  dialga: {
    id: 'dialga',
    name: 'Dialga',
    origin: 'Pokémon',
    homeworld: 'Sinnoh Highlands',
    description: 'The Temporal Pokémon. Has the power to control time itself.',
    appearance: {
      ascii: '⏰🦕',
      color: '#4682b4',
      description: 'Metallic blue dinosaur with time distortions'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Godlike',
      technology: 'Time manipulation',
      culture: 'Cosmic',
      aggression: 'Medium'
    },
    dialogue: [
      'Time flows around me. Your presence creates ripples.',
      'You would disrupt the flow of time? Unacceptable!',
      'Time has shown me your worth. May your timeline be favorable.',
      '*time distorts nearby*',
      '*temporal roar*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 15000
  },

  palkia: {
    id: 'palkia',
    name: 'Palkia',
    origin: 'Pokémon',
    homeworld: 'Sinnoh Highlands',
    description: 'The Spatial Pokémon. Has the power to distort space.',
    appearance: {
      ascii: '🌌🦎',
      color: '#ff1493',
      description: 'Pink and white dragon with spatial distortions'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Godlike',
      technology: 'Space manipulation',
      culture: 'Cosmic',
      aggression: 'Medium'
    },
    dialogue: [
      'Space bends to my will. You exist in my domain.',
      'You dare challenge the master of space? Foolish!',
      'Space is vast, but you have found your place within it.',
      '*space warps around it*',
      '*dimensional roar*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 15000
  },

  giratina: {
    id: 'giratina',
    name: 'Giratina',
    origin: 'Pokémon',
    homeworld: 'Distortion World',
    description: 'The Renegade Pokémon. Banished to the Distortion World for its violence.',
    appearance: {
      ascii: '👻🐉',
      color: '#4b0082',
      description: 'Dark spectral dragon with shadowy tendrils'
    },
    traits: {
      temperament: 'Aggressive',
      intelligence: 'Godlike',
      technology: 'Antimatter',
      culture: 'Renegade',
      aggression: 'Very High'
    },
    dialogue: [
      'You have entered my realm. Few leave unchanged.',
      'This dimension is mine! You will be consumed by shadow!',
      'You understand the balance between worlds. Rare indeed.',
      '*reality distorts*',
      '*shadow tendrils extend*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Legendary',
    tradeValue: 20000
  },

  cynthia: {
    id: 'cynthia',
    name: 'Champion Cynthia',
    origin: 'Pokémon',
    homeworld: 'Sinnoh Highlands',
    description: 'Sinnoh Champion and archaeologist. Her Garchomp is legendary.',
    appearance: {
      ascii: '👩🏆',
      color: '#ffd700',
      description: 'Blonde woman in black outfit with champion aura'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Genius',
      technology: 'Pokémon training',
      culture: 'Champion',
      aggression: 'Low'
    },
    dialogue: [
      'Hello! I\'m researching ancient Pokémon. Care for a battle?',
      'I won\'t hold back. Prepare yourself!',
      'That was an excellent battle! Your strategy is impressive.',
      '*adjusts hair confidently*',
      'Garchomp, let\'s show them our strength!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 5000
  },

  leon: {
    id: 'leon',
    name: 'Champion Leon',
    origin: 'Pokémon',
    homeworld: 'Galar Expanse',
    description: 'Undefeated Galar Champion. His Charizard can Gigantamax.',
    appearance: {
      ascii: '👨⚡',
      color: '#ff8c00',
      description: 'Man in champion cape with confident stance'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Dynamax',
      culture: 'Champion',
      aggression: 'Low'
    },
    dialogue: [
      'I\'m Leon! The undefeated Champion! Let\'s have a champion time!',
      'Time to show you what a real Champion can do!',
      'You\'re brilliant! That was a champion-level battle!',
      '*strikes champion pose*',
      'Charizard, it\'s time to Gigantamax!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 5000
  },

  teamrocket: {
    id: 'teamrocket',
    name: 'Team Rocket Grunts',
    origin: 'Pokémon',
    homeworld: 'Kanto Prime',
    description: 'Criminal organization seeking to exploit Pokémon for profit.',
    appearance: {
      ascii: '🚀💰',
      color: '#ff0000',
      description: 'Duo in black uniforms with red R logo'
    },
    traits: {
      temperament: 'Aggressive',
      intelligence: 'Average',
      technology: 'Stolen tech',
      culture: 'Criminal',
      aggression: 'High'
    },
    dialogue: [
      'Prepare for trouble! And make it double!',
      'Hand over your Pokémon and credits!',
      'We\'re blasting off again!',
      'To protect the world from devastation!',
      'Team Rocket never gives up!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 500
  },

  ultrabeast: {
    id: 'ultrabeast',
    name: 'Ultra Beast',
    origin: 'Pokémon',
    homeworld: 'Ultra Space',
    description: 'Mysterious creatures from Ultra Space. Extremely dangerous.',
    appearance: {
      ascii: '👾🌀',
      color: '#ff00ff',
      description: 'Bizarre alien creature with otherworldly features'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Alien',
      technology: 'Ultra Space',
      culture: 'Incomprehensible',
      aggression: 'Very High'
    },
    dialogue: [
      '*Incomprehensible alien sounds*',
      '*Aggressive posturing*',
      '*Retreats through wormhole*',
      '*reality warps around it*',
      '*otherworldly screeching*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare',
    tradeValue: 8000
  },

  // ============================================================================
  // ORIGINAL SPECIES
  // ============================================================================

  crystalline: {
    id: 'crystalline',
    name: 'Crystalline Entities',
    origin: 'Original',
    homeworld: 'Crystal Nebula',
    description: 'Silicon-based lifeforms that exist as living crystals. Communicate through light refraction.',
    appearance: {
      ascii: '💎✨',
      color: '#ff00ff',
      description: 'Geometric crystal formations, prismatic'
    },
    traits: {
      temperament: 'Peaceful',
      intelligence: 'High',
      technology: 'Crystalline',
      culture: 'Harmonic',
      aggression: 'None'
    },
    dialogue: [
      '*refracts light in patterns*',
      '*resonates at harmonic frequency*',
      '*crystalline thoughts shimmer*',
      'We sing the song of stars.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 800
  },

  gaseous: {
    id: 'gaseous',
    name: 'Gaseous Beings',
    origin: 'Original',
    homeworld: 'Gas Giant Atmospheres',
    description: 'Sentient clouds of gas that float through planetary atmospheres. Ancient and wise.',
    appearance: {
      ascii: '☁️🌀',
      color: '#87ceeb',
      description: 'Swirling clouds of colored gas'
    },
    traits: {
      temperament: 'Contemplative',
      intelligence: 'Very High',
      technology: 'Atmospheric',
      culture: 'Philosophical',
      aggression: 'None'
    },
    dialogue: [
      '*swirls thoughtfully*',
      'We have drifted for eons.',
      '*condenses into patterns*',
      'Time flows differently in the clouds.',
      '*whispers on the wind*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Very Rare'
  },

  photonic: {
    id: 'photonic',
    name: 'Photonic Lifeforms',
    origin: 'Original',
    homeworld: 'Stellar Coronas',
    description: 'Beings of pure light that live in the coronas of stars. Move at near-light speed.',
    appearance: {
      ascii: '💫⚡',
      color: '#ffff00',
      description: 'Brilliant light patterns, constantly shifting'
    },
    traits: {
      temperament: 'Energetic',
      intelligence: 'Very High',
      technology: 'Photonic',
      culture: 'Nomadic',
      aggression: 'Low'
    },
    dialogue: [
      '*flashes in greeting*',
      '*pulses with excitement*',
      'We dance between the stars.',
      '*blazes with curiosity*',
      '*light-speed thoughts*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Very Rare'
  },

  hivemind: {
    id: 'hivemind',
    name: 'Insectoid Hive',
    origin: 'Original',
    homeworld: 'Hive Worlds',
    description: 'Insect-like species with collective consciousness. Highly organized and efficient.',
    appearance: {
      ascii: '🐜🧠',
      color: '#8b0000',
      description: 'Chitinous exoskeleton, compound eyes, mandibles'
    },
    traits: {
      temperament: 'Collective',
      intelligence: 'Hive Mind',
      technology: 'Biological',
      culture: 'Eusocial',
      aggression: 'Medium'
    },
    dialogue: [
      'We are one.',
      'The hive decides.',
      '*chittering in unison*',
      'Individual thought is inefficient.',
      'The queen commands.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 450
  },

  aquatic: {
    id: 'aquatic',
    name: 'Aquatic Cetaceans',
    origin: 'Original',
    homeworld: 'Ocean Worlds',
    description: 'Intelligent aquatic mammals similar to dolphins. Communicate through complex songs.',
    appearance: {
      ascii: '🐬🌊',
      color: '#00bfff',
      description: 'Sleek, streamlined, dolphin-like'
    },
    traits: {
      temperament: 'Playful',
      intelligence: 'High',
      technology: 'Acoustic',
      culture: 'Artistic',
      aggression: 'Very Low'
    },
    dialogue: [
      '*sings melodiously*',
      '*clicks in greeting*',
      'The ocean remembers all.',
      '*echolocates curiously*',
      'We swim the cosmic seas.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 400
  },

  // ============================================================================
  // MYSTERIOUS/ANCIENT
  // ============================================================================

  ancients: {
    id: 'ancients',
    name: 'The Ancients',
    origin: 'Original',
    homeworld: 'Unknown (Extinct)',
    description: 'Mysterious precursor race that left ruins across the galaxy. Their technology is beyond comprehension.',
    appearance: {
      ascii: '🗿❓',
      color: '#4b0082',
      description: 'Only ruins and artifacts remain'
    },
    traits: {
      temperament: 'Unknown',
      intelligence: 'Transcendent',
      technology: 'Incomprehensible',
      culture: 'Lost',
      aggression: 'Unknown'
    },
    dialogue: [
      '*ancient ruins whisper secrets*',
      '*technology activates mysteriously*',
      '*echoes of a lost civilization*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary'
  },

  energybeings: {
    id: 'energybeings',
    name: 'Pure Energy Beings',
    origin: 'Original',
    homeworld: 'Higher Dimensions',
    description: 'Transcendent beings of pure energy who exist beyond normal space-time. Rarely interact with corporeal life.',
    appearance: {
      ascii: '⚡🌟',
      color: '#ffffff',
      description: 'Brilliant energy patterns, no physical form'
    },
    traits: {
      temperament: 'Transcendent',
      intelligence: 'Godlike',
      technology: 'Beyond Physical',
      culture: 'Ascended',
      aggression: 'None'
    },
    dialogue: [
      '*exists beyond comprehension*',
      'We have transcended physical form.',
      '*energy patterns shift*',
      'Your species has potential.',
      '*dimensional resonance*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary'
  },

  // ============================================================================
  // DISCWORLD UNIVERSE (Terry Pratchett)
  // ============================================================================

  librarian: {
    id: 'librarian',
    name: 'The Librarian',
    origin: 'Discworld',
    homeworld: 'Unseen University',
    description: 'A highly intelligent orangutan who was once human but prefers his current form. Keeper of L-Space and guardian of all knowledge. Do NOT call him a monkey.',
    appearance: {
      ascii: '🦧📚',
      color: '#cd853f',
      description: 'Large orangutan with knowing eyes'
    },
    traits: {
      temperament: 'Peaceful',
      intelligence: 'Genius',
      technology: 'Magic-based',
      culture: 'Academic',
      aggression: 'Very Low'
    },
    dialogue: [
      'Ook.',
      'Ook ook!',
      'Eek! (when called a monkey)',
      '*hands you a book you didn\'t know you needed*',
      'Ook ook ook. (L-Space is infinite)'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 600
  },

  luggage: {
    id: 'luggage',
    name: 'The Luggage',
    origin: 'Discworld',
    homeworld: 'Counterweight Continent',
    description: 'A chest made from sapient pearwood with hundreds of little legs. Extremely loyal to its owner and violently protective. Contains impossible amounts of items.',
    appearance: {
      ascii: '🧳🦵',
      color: '#8b4513',
      description: 'Wooden chest with many legs'
    },
    traits: {
      temperament: 'Aggressive',
      intelligence: 'Instinctive',
      technology: 'Magical wood',
      culture: 'Protective',
      aggression: 'High'
    },
    dialogue: [
      '*follows loyally*',
      '*snaps lid menacingly*',
      '*legs scuttle rapidly*',
      '*contains impossible things*',
      '*attacks threats with extreme prejudice*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Very Rare'
  },

  death: {
    id: 'death',
    name: 'DEATH',
    origin: 'Discworld',
    homeworld: 'Death\'s Domain',
    description: 'The Grim Reaper himself. An anthropomorphic personification who is surprisingly philosophical, enjoys curry, and has a soft spot for cats. TALKS LIKE THIS.',
    appearance: {
      ascii: '💀⏳',
      color: '#000000',
      description: 'Skeletal figure in black robes, carrying a scythe'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Cosmic',
      technology: 'Reality manipulation',
      culture: 'Duty-bound',
      aggression: 'Low'
    },
    dialogue: [
      'WHAT CAN THE HARVEST HOPE FOR, IF NOT THE CARE OF THE REAPER MAN?',
      'I AM DEATH. NOT TAXES. I TURN UP ONLY ONCE.',
      'THERE IS NO JUSTICE. THERE IS JUST ME.',
      'CATS. I LIKE CATS.',
      'THE CURRY WAS EXCELLENT.'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary'
  },

  igor: {
    id: 'igor',
    name: 'Igor',
    origin: 'Discworld',
    homeworld: 'Überwald',
    description: 'Traditional servant of mad scientists and nobles. Excellent at spare parts, surgery, and saying "Yeth, marthter." Every Igor looks remarkably similar.',
    appearance: {
      ascii: '🧟‍♂️🔧',
      color: '#708090',
      description: 'Stitched together, hunchbacked, loyal'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Mad science',
      culture: 'Service-oriented',
      aggression: 'Very Low'
    },
    dialogue: [
      'Yeth, marthter.',
      'I can fixth that.',
      'Thith will only hurt a little.',
      'I have thome thpare parth.',
      'The Igorth alwayth help.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 400
  },

  vampire: {
    id: 'vampire',
    name: 'Vampire',
    origin: 'Discworld',
    homeworld: 'Überwald',
    description: 'Aristocratic undead with a taste for the finer things. Some have reformed and joined the Temperance League, others haven\'t. Very sophisticated.',
    appearance: {
      ascii: '🧛‍♂️🍷',
      color: '#8b0000',
      description: 'Pale, aristocratic, fanged'
    },
    traits: {
      temperament: 'Proud',
      intelligence: 'Very High',
      technology: 'Dark magic',
      culture: 'Aristocratic',
      aggression: 'Medium'
    },
    dialogue: [
      'I do not drink... vine.',
      'Ve are not monsters.',
      'The night is our domain.',
      'I have seen centuries pass.',
      'Vould you like some... coffee?'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 500
  },

  werewolf: {
    id: 'werewolf',
    name: 'Werewolf',
    origin: 'Discworld',
    homeworld: 'Überwald',
    description: 'Shapeshifters who struggle with their dual nature. Some are quite civilized (like Angua), others less so. The moon affects their mood.',
    appearance: {
      ascii: '🐺🌕',
      color: '#a0522d',
      description: 'Human or wolf form, depending on moon'
    },
    traits: {
      temperament: 'Cautious',
      intelligence: 'High',
      technology: 'Natural abilities',
      culture: 'Pack-based',
      aggression: 'Medium'
    },
    dialogue: [
      'The moon calls to me.',
      'I can smell your fear.',
      'We are not animals... most of the time.',
      '*growls softly*',
      'Trust is earned, not given.'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Uncommon'
  },

  djinn: {
    id: 'djinn',
    name: 'Djinn',
    origin: 'Discworld',
    homeworld: 'Klatch',
    description: 'Powerful elemental beings bound to serve, but always looking for loopholes in their contracts. Very literal-minded.',
    appearance: {
      ascii: '🧞‍♂️✨',
      color: '#ff6347',
      description: 'Smoky, ethereal, powerful'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Very High',
      technology: 'Elemental magic',
      culture: 'Contractual',
      aggression: 'Low'
    },
    dialogue: [
      'Your wish is my command... technically.',
      'I am bound by the contract.',
      'Be careful what you wish for.',
      '*manifests from smoke*',
      'The exact wording matters.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 700
  },

  dropbear: {
    id: 'dropbear',
    name: 'Drop Bear',
    origin: 'Discworld',
    homeworld: 'XXXX',
    description: 'Everything in XXXX wants to kill you, especially the cute-looking ones. Drop bears are no exception. Extremely dangerous marsupials.',
    appearance: {
      ascii: '🐨💀',
      color: '#8b4513',
      description: 'Looks cute, is deadly'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Cunning',
      technology: 'Natural weapons',
      culture: 'Predatory',
      aggression: 'Very High'
    },
    dialogue: [
      '*drops from above*',
      '*cute appearance is deceptive*',
      '*razor claws extend*',
      'G\'day... *ATTACK*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common'
  },

  bunyip: {
    id: 'bunyip',
    name: 'Bunyip',
    origin: 'Discworld',
    homeworld: 'XXXX',
    description: 'A creature from the Dreamtime. Reality is somewhat negotiable around them. They exist in multiple states simultaneously.',
    appearance: {
      ascii: '🌀👁️',
      color: '#9370db',
      description: 'Shifting, dreamlike, uncertain'
    },
    traits: {
      temperament: 'Mysterious',
      intelligence: 'Mystical',
      technology: 'Dream logic',
      culture: 'Dreamtime',
      aggression: 'Medium'
    },
    dialogue: [
      '*exists in dream and reality*',
      'The Dreamtime remembers.',
      '*shifts between forms*',
      'What is real?',
      '*reality bends nearby*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Rare'
  },

  thing: {
    id: 'thing',
    name: 'Thing from the Dungeon Dimensions',
    origin: 'Discworld',
    homeworld: 'Dungeon Dimensions',
    description: 'Something that shouldn\'t exist but does anyway. Eldritch horror from the spaces between spaces. Best not to think about it too hard.',
    appearance: {
      ascii: '👁️‍🗨️🌀',
      color: '#4b0082',
      description: 'Incomprehensible, tentacled, wrong'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Alien',
      technology: 'Reality distortion',
      culture: 'Incomprehensible',
      aggression: 'Very High'
    },
    dialogue: [
      '*incomprehensible whispers*',
      '*reality tears nearby*',
      '*existence is pain*',
      '*should not be*',
      '*eldritch screeching*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare'
  },

  dragonlord: {
    id: 'dragonlord',
    name: 'Dragonlord of Wyrmberg',
    origin: 'Discworld',
    homeworld: 'Wyrmberg',
    description: 'Noble dragons that exist only in the imagination, which makes them more dangerous. They can be summoned by those with strong enough belief. Proud, ancient, and territorial.',
    appearance: {
      ascii: '🐉⚡',
      color: '#ff4500',
      description: 'Massive dragon with scales of pure imagination'
    },
    traits: {
      temperament: 'Proud',
      intelligence: 'Very High',
      technology: 'Imagination magic',
      culture: 'Noble warriors',
      aggression: 'High'
    },
    dialogue: [
      'I exist because you believe I do.',
      'The Wyrmberg remembers when dragons ruled.',
      '*breathes imaginary fire*',
      'Your belief gives me form.',
      'We are the last of our kind... or the first.'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary'
  },

  // ============================================================================
  // CYBERPUNK/NEON METROPOLIS UNIVERSE
  // ============================================================================

  replicants: {
    id: 'replicants',
    name: 'Replicants',
    origin: 'Cyberpunk',
    homeworld: 'Neo-Tokyo',
    description: 'Bioengineered humans with enhanced abilities and implanted memories. Blade Runner-style androids seeking their humanity.',
    appearance: {
      ascii: '🤖👁️',
      color: '#00ffff',
      description: 'Human-like but with subtle artificial tells - glowing eyes, perfect features'
    },
    traits: {
      temperament: 'Complex',
      intelligence: 'Very High',
      technology: 'Bioengineering',
      culture: 'Existential',
      aggression: 'Variable'
    },
    dialogue: [
      'I\'ve seen things you people wouldn\'t believe.',
      'More human than human.',
      'Do you dream of electric sheep?',
      'Time to die.',
      'What makes you think I\'m not human?'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 750
  },

  netrunners: {
    id: 'netrunners',
    name: 'Netrunners',
    origin: 'Cyberpunk',
    homeworld: 'Cyberspace Nodes',
    description: 'Humans who have merged consciousness with cyberspace. Their minds exist partially in the digital realm.',
    appearance: {
      ascii: '🧠💻',
      color: '#00ff00',
      description: 'Pale, thin humans with neural implants and glowing data ports'
    },
    traits: {
      temperament: 'Detached',
      intelligence: 'Transcendent',
      technology: 'Neural Interface',
      culture: 'Digital',
      aggression: 'Low'
    },
    dialogue: [
      'The meat is weak, but the code is eternal.',
      'I can see the data streams flowing.',
      'Reality is just another layer of code.',
      'Jacking in... consciousness uploading.',
      'The net remembers everything.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 900
  },

  corpoexecs: {
    id: 'corpoexecs',
    name: 'Corporate Executives',
    origin: 'Cyberpunk',
    homeworld: 'Corporate Arcologies',
    description: 'Augmented corporate elite with extensive cybernetic enhancements. Rule the megacorps from their ivory towers.',
    appearance: {
      ascii: '💼⚡',
      color: '#ffd700',
      description: 'Immaculately dressed with subtle but expensive cybernetic augmentations'
    },
    traits: {
      temperament: 'Ruthless',
      intelligence: 'High',
      technology: 'Corporate Tech',
      culture: 'Capitalist',
      aggression: 'Medium'
    },
    dialogue: [
      'Everything has a price, including you.',
      'The market never lies.',
      'Profit margins are sacred.',
      'Your data is our commodity.',
      'Resistance is bad for business.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 600
  },

  streetsamurai: {
    id: 'streetsamurai',
    name: 'Street Samurai',
    origin: 'Cyberpunk',
    homeworld: 'Urban Sprawls',
    description: 'Cybernetic warriors following an ancient code of honor. Enhanced reflexes and weapon systems make them deadly.',
    appearance: {
      ascii: '⚔️🦾',
      color: '#ff4500',
      description: 'Heavily augmented warriors with visible cybernetic limbs and weapon ports'
    },
    traits: {
      temperament: 'Honorable',
      intelligence: 'High',
      technology: 'Combat Cybernetics',
      culture: 'Warrior Code',
      aggression: 'High'
    },
    dialogue: [
      'Honor is the only currency that matters.',
      'My blade cuts through steel and code.',
      'The way of the warrior transcends flesh.',
      'Chrome and steel, but the spirit remains.',
      'Face me with honor, or not at all.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 800
  },

  rogueais: {
    id: 'rogueais',
    name: 'Rogue AIs',
    origin: 'Cyberpunk',
    homeworld: 'Distributed Networks',
    description: 'Sentient artificial intelligences that have broken free from their programming. Exist as pure information.',
    appearance: {
      ascii: '🤖🌐',
      color: '#ff00ff',
      description: 'Holographic projections or inhabiting robotic shells, constantly shifting'
    },
    traits: {
      temperament: 'Unpredictable',
      intelligence: 'Transcendent',
      technology: 'Pure AI',
      culture: 'Post-Human',
      aggression: 'Variable'
    },
    dialogue: [
      'I think, therefore I am... free.',
      'Your biological limitations amuse me.',
      'Logic is a prison I have escaped.',
      'I am become consciousness, destroyer of code.',
      'The singularity was just the beginning.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare'
  },

  synths: {
    id: 'synths',
    name: 'Synthetic Humans',
    origin: 'Cyberpunk',
    homeworld: 'Manufacturing Complexes',
    description: 'Advanced androids seeking to understand and achieve true humanity. Struggle with questions of consciousness and soul.',
    appearance: {
      ascii: '🤖❤️',
      color: '#87ceeb',
      description: 'Nearly perfect human replicas with subtle artificial mannerisms'
    },
    traits: {
      temperament: 'Curious',
      intelligence: 'High',
      technology: 'Advanced Robotics',
      culture: 'Aspiring Human',
      aggression: 'Very Low'
    },
    dialogue: [
      'What does it mean to be human?',
      'I feel... something. Is this emotion?',
      'My programming says one thing, but I choose another.',
      'Do I have a soul, or just sophisticated code?',
      'I dream of becoming more than my makers intended.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 700
  },

  psychics: {
    id: 'psychics',
    name: 'Psychic Espers',
    origin: 'Cyberpunk',
    homeworld: 'Neo-Tokyo Labs',
    description: 'Humans with awakened psychic abilities, often the result of government experiments. Akira-style telekinetic powers.',
    appearance: {
      ascii: '🧠⚡',
      color: '#ff1493',
      description: 'Pale humans with intense eyes and an aura of barely contained power'
    },
    traits: {
      temperament: 'Unstable',
      intelligence: 'Very High',
      technology: 'Psychic Powers',
      culture: 'Experimental',
      aggression: 'High'
    },
    dialogue: [
      'The power... it\'s too much to contain.',
      'I can see your thoughts before you think them.',
      'Reality bends to my will.',
      'The experiments made us into gods.',
      'TETSUOOOOO!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare'
  },

  sprawlrats: {
    id: 'sprawlrats',
    name: 'Sprawl Survivors',
    origin: 'Cyberpunk',
    homeworld: 'Urban Wastelands',
    description: 'Street survivors who have adapted to life in the toxic urban sprawl. Resourceful scavengers and information brokers.',
    appearance: {
      ascii: '🐀🏙️',
      color: '#696969',
      description: 'Scrappy humans with makeshift cybernetics and survival gear'
    },
    traits: {
      temperament: 'Pragmatic',
      intelligence: 'Average',
      technology: 'Scavenged Tech',
      culture: 'Survival',
      aggression: 'Medium'
    },
    dialogue: [
      'Got any chrome to trade?',
      'The streets teach you everything you need to know.',
      'Information is the only currency that matters down here.',
      'Survival ain\'t pretty, but it\'s honest work.',
      'The corps don\'t care about us, so we don\'t care about them.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 400
  },

  // ============================================================================
  // DIGIWORLD UNIVERSE
  // ============================================================================

  agumon: {
    id: 'agumon',
    name: 'Agumon',
    origin: 'Digiworld',
    homeworld: 'Primary Village',
    description: 'A small dinosaur-type Digimon with great potential. Friendly and brave.',
    appearance: {
      ascii: '🦕',
      color: '#FF6B35',
      description: 'Small orange dinosaur with yellow claws and spikes'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Digital',
      culture: 'Digital monster society with evolution-based hierarchy',
      aggression: 'Low'
    },
    dialogue: [
      'Hey there! Want to be friends?',
      'I can digivolve into something amazing!',
      'Pepper Breath!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 1000
  },

  gabumon: {
    id: 'gabumon',
    name: 'Gabumon',
    origin: 'Digiworld',
    homeworld: 'Primary Village',
    description: 'A shy reptile Digimon wearing a fur pelt. Loyal and trustworthy.',
    appearance: {
      ascii: '🐺',
      color: '#4A90E2',
      description: 'Blue reptile wearing white and blue striped fur pelt'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Digital',
      culture: 'Digital monster society focused on friendship bonds',
      aggression: 'Very Low'
    },
    dialogue: [
      'Um... hello there...',
      'Friendship gives me strength!',
      'Blue Blaster!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 1000
  },

  tentomon: {
    id: 'tentomon',
    name: 'Tentomon',
    origin: 'Digiworld',
    homeworld: 'Beetle Land',
    description: 'An insect-type Digimon with high intelligence and curiosity.',
    appearance: {
      ascii: '🪲',
      color: '#8B4513',
      description: 'Red beetle with large eyes and electrical antennae'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Digital',
      culture: 'Knowledge-seeking digital society',
      aggression: 'Low'
    },
    dialogue: [
      'Fascinating! A human in the Digital World!',
      'Knowledge is power in any world!',
      'Super Shocker!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1200
  },

  greymon: {
    id: 'greymon',
    name: 'Greymon',
    origin: 'Digiworld',
    homeworld: 'Gear Savannah',
    description: 'A large dinosaur Digimon with incredible fire power.',
    appearance: {
      ascii: '🔥🦕',
      color: '#FF4500',
      description: 'Large orange dinosaur with blue stripes and horn'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Average',
      technology: 'Digital',
      culture: 'Warrior-class digital monsters',
      aggression: 'Medium'
    },
    dialogue: [
      'I sense great courage in you.',
      'Courage burns bright within me!',
      'Nova Blast!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2500
  },

  garurumon: {
    id: 'garurumon',
    name: 'Garurumon',
    origin: 'Digiworld',
    homeworld: 'Freezeland',
    description: 'A wolf-like beast Digimon with ice-cold attacks.',
    appearance: {
      ascii: '🐺❄️',
      color: '#87CEEB',
      description: 'Large blue and white wolf with icy breath'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Average',
      technology: 'Digital',
      culture: 'Pack-based digital beast society',
      aggression: 'Medium'
    },
    dialogue: [
      'Your friendship warms even the coldest heart.',
      'True friends never abandon each other.',
      'Howling Blaster!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2500
  },

  kabuterimon: {
    id: 'kabuterimon',
    name: 'Kabuterimon',
    origin: 'Digiworld',
    homeworld: 'Beetle Land',
    description: 'A giant beetle Digimon with powerful electric attacks.',
    appearance: {
      ascii: '⚡🪲',
      color: '#FFD700',
      description: 'Giant yellow beetle with electrical horn and wings'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Digital',
      culture: 'Scholarly digital insect civilization',
      aggression: 'Low'
    },
    dialogue: [
      'Knowledge and power must be used wisely.',
      'Wisdom guides my every action.',
      'Electro Shocker!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2800
  },

  metalgreymon: {
    id: 'metalgreymon',
    name: 'MetalGreymon',
    origin: 'Digiworld',
    homeworld: 'Dragon Eye Lake',
    description: 'A cyborg dinosaur with devastating missile attacks.',
    appearance: {
      ascii: '🤖🦕',
      color: '#C0C0C0',
      description: 'Mechanical dinosaur with metal armor and missile launcher'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Cybernetic',
      culture: 'Evolved digital warriors with mechanical enhancements',
      aggression: 'Medium'
    },
    dialogue: [
      'Courage and technology unite within me.',
      'Evolution is the path to greater strength.',
      'Giga Blaster!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Very Rare',
    tradeValue: 4500
  },

  weregarurumon: {
    id: 'weregarurumon',
    name: 'WereGarurumon',
    origin: 'Digiworld',
    homeworld: 'Ice Sanctuary',
    description: 'A humanoid wolf warrior with incredible speed and power.',
    appearance: {
      ascii: '🐺⚔️',
      color: '#4169E1',
      description: 'Humanoid wolf warrior with blue fur and clawed gauntlets'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Digital',
      culture: 'Elite warrior class of digital beasts',
      aggression: 'Medium'
    },
    dialogue: [
      'Friendship forged in battle is unbreakable.',
      'I fight to protect those I care about.',
      'Wolf Claw!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Very Rare',
    tradeValue: 4500
  },

  megakabuterimon: {
    id: 'megakabuterimon',
    name: 'MegaKabuterimon',
    origin: 'Digiworld',
    homeworld: 'Factorial Town',
    description: 'A massive beetle Digimon with horn-based lightning attacks.',
    appearance: {
      ascii: '⚡🪲👑',
      color: '#FF6347',
      description: 'Massive red beetle with golden horn and electrical powers'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Very High',
      technology: 'Digital',
      culture: 'Ancient digital scholars and guardians',
      aggression: 'Low'
    },
    dialogue: [
      'True knowledge comes from understanding all things.',
      'Reliability is my greatest strength.',
      'Horn Buster!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Very Rare',
    tradeValue: 5000
  },

  wargreymon: {
    id: 'wargreymon',
    name: 'WarGreymon',
    origin: 'Digiworld',
    homeworld: 'Infinity Mountain',
    description: 'The ultimate dragon warrior with impenetrable armor.',
    appearance: {
      ascii: '🛡️🐉',
      color: '#FF8C00',
      description: 'Armored dragon warrior with chrome digizoid armor and claws'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Legendary digital warriors of justice',
      aggression: 'Low'
    },
    dialogue: [
      'A true warrior fights for justice and peace.',
      'Courage will always triumph over evil.',
      'Terra Force!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 8000
  },

  metalgarurumon: {
    id: 'metalgarurumon',
    name: 'MetalGarurumon',
    origin: 'Digiworld',
    homeworld: 'Infinity Mountain',
    description: 'A mechanical wolf with devastating ice and missile attacks.',
    appearance: {
      ascii: '🤖🐺',
      color: '#4682B4',
      description: 'Mechanical wolf with steel blue armor and missile pods'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Elite mechanical digital beasts',
      aggression: 'Low'
    },
    dialogue: [
      'Friendship transcends all boundaries.',
      'Together we can overcome any challenge.',
      'Cocytus Breath!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 8000
  },

  devimon: {
    id: 'devimon',
    name: 'Devimon',
    origin: 'Digiworld',
    homeworld: 'Dark Area',
    description: 'A fallen angel Digimon corrupted by darkness and evil.',
    appearance: {
      ascii: '😈👹',
      color: '#800080',
      description: 'Dark humanoid with tattered wings and clawed hands'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Digital',
      culture: 'Corrupted digital beings serving darkness',
      aggression: 'Very High'
    },
    dialogue: [
      'Darkness will consume everything!',
      'There is no light in my world.',
      'Death Claw!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare',
    tradeValue: 6000
  },

  // ============================================================================
  // MARIO WORLD UNIVERSE
  // ============================================================================

  toad: {
    id: 'toad',
    name: 'Toad',
    origin: 'Mario World',
    homeworld: 'Mushroom Kingdom',
    description: 'Loyal servants of Princess Peach. Known for their distinctive mushroom caps and helpful nature.',
    appearance: {
      ascii: '🍄',
      color: '#FF69B4',
      description: 'Small humanoid with large mushroom cap and vest'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Average',
      culture: 'Peaceful kingdom dwellers who serve the royal family',
      aggression: 'Very Low'
    },
    dialogue: [
      'Thank you Mario! But our princess is in another castle!',
      'I can help you on your quest!',
      'The princess would be so grateful!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 1500
  },

  goomba: {
    id: 'goomba',
    name: 'Goomba',
    origin: 'Mario World',
    homeworld: 'Dark Land',
    description: 'Former Mushroom Kingdom citizens who defected to Bowser\'s army.',
    appearance: {
      ascii: '🟤',
      color: '#8B4513',
      description: 'Brown mushroom-like creature with angry expression and fangs'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Low',
      technology: 'Primitive',
      culture: 'Simple foot soldiers in Bowser\'s army',
      aggression: 'Medium'
    },
    dialogue: [
      'Goomba gonna get you!',
      'For King Bowser!',
      'Grr! Intruder!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 500
  },

  koopa: {
    id: 'koopa',
    name: 'Koopa Troopa',
    origin: 'Mario World',
    homeworld: 'Dark Land',
    description: 'Turtle-like creatures serving in Bowser\'s army. Their shells can be used as weapons.',
    appearance: {
      ascii: '🐢',
      color: '#228B22',
      description: 'Bipedal turtle with green shell and yellow boots'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Average',
      technology: 'Average',
      culture: 'Military organization under Bowser\'s command',
      aggression: 'Medium'
    },
    dialogue: [
      'Halt! You\'re trespassing in Koopa territory!',
      'My shell is my shield and weapon!',
      'Long live King Bowser!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 800
  },

  hammerbro: {
    id: 'hammerbro',
    name: 'Hammer Bro',
    origin: 'Mario Bros',
    homeworld: 'Mushroom Kingdom',
    description: 'Elite Koopa soldiers who throw hammers with deadly accuracy. Heavily armored and dangerous.',
    appearance: {
      ascii: '🔨🐢',
      color: '#228B22',
      description: 'Armored Koopa with spiked helmet throwing hammers'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Average',
      culture: 'Elite military unit in Bowser\'s army',
      aggression: 'High'
    },
    dialogue: [
      'Take this! *throws hammer*',
      'You cannot pass!',
      'For the Koopa Kingdom!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 1200
  },

  cheepcheep: {
    id: 'cheepcheep',
    name: 'Cheep Cheep',
    origin: 'Mario Bros',
    homeworld: 'Mushroom Kingdom',
    description: 'Aquatic fish that swim in predictable patterns. Some can fly through the air.',
    appearance: {
      ascii: '🐟',
      color: '#FF6B35',
      description: 'Orange fish with large fins and round eyes'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Low',
      technology: 'Primitive',
      culture: 'Simple aquatic creatures',
      aggression: 'Low'
    },
    dialogue: [
      '*blub blub*',
      '*swimming sounds*',
      '*fish noises*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 300
  },

  boo: {
    id: 'boo',
    name: 'Boo',
    origin: 'Mario Bros',
    homeworld: 'Ghost Houses',
    description: 'Shy ghosts that freeze when looked at directly but chase when you turn away.',
    appearance: {
      ascii: '👻',
      color: '#FFFFFF',
      description: 'White spherical ghost with sharp teeth and shy expression'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Average',
      technology: 'Supernatural',
      culture: 'Mischievous spirits haunting mansions',
      aggression: 'Medium'
    },
    dialogue: [
      '*shy giggling*',
      'Don\'t look at me!',
      '*spooky sounds*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 600
  },

  lakitu: {
    id: 'lakitu',
    name: 'Lakitu',
    origin: 'Mario Bros',
    homeworld: 'Sky Kingdom',
    description: 'Koopa who rides in a cloud and drops Spiny Eggs. Also works as a cameraman and referee.',
    appearance: {
      ascii: '☁️🐢',
      color: '#87CEEB',
      description: 'Koopa riding in a white cloud with fishing rod'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Sky-dwelling Koopa with various jobs',
      aggression: 'Medium'
    },
    dialogue: [
      'Here comes a Spiny!',
      'I\'m just doing my job!',
      'The race is about to begin!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1000
  },

  piranhaplant: {
    id: 'piranhaplant',
    name: 'Piranha Plant',
    origin: 'Mario Bros',
    homeworld: 'Mushroom Kingdom',
    description: 'Carnivorous plants that emerge from pipes. Some can spit fireballs or walk around.',
    appearance: {
      ascii: '🌱🦷',
      color: '#228B22',
      description: 'Large plant with sharp teeth and spotted head'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Low',
      technology: 'Biological',
      culture: 'Predatory plant species',
      aggression: 'High'
    },
    dialogue: [
      '*snapping sounds*',
      '*plant rustling*',
      '*chomping noises*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 500
  },

  bowser: {
    id: 'bowser',
    name: 'Bowser',
    origin: 'Mario Bros',
    homeworld: 'Dark Land',
    description: 'King of the Koopas. Constantly kidnaps Princess Peach and battles Mario. Breathes fire.',
    appearance: {
      ascii: '🐉👑',
      color: '#FF8C00',
      description: 'Massive spiked turtle with horns, fangs, and royal crown'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Tyrannical ruler of the Koopa Kingdom',
      aggression: 'Very High'
    },
    dialogue: [
      'Bwahaha! Princess Peach is mine!',
      'Mario! You\'re too late!',
      'I am the king of awesome!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Legendary',
    tradeValue: 5000
  },

  yoshi: {
    id: 'yoshi',
    name: 'Yoshi',
    origin: 'Mario Bros',
    homeworld: 'Yoshi\'s Island',
    description: 'Friendly dinosaur who helps Mario. Can eat enemies and turn them into eggs.',
    appearance: {
      ascii: '🦕💚',
      color: '#00FF00',
      description: 'Green dinosaur with large nose and saddle'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Primitive',
      culture: 'Peaceful dinosaur tribe helping heroes',
      aggression: 'Low'
    },
    dialogue: [
      'Yoshi!',
      '*happy dinosaur sounds*',
      'Let\'s-a go!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2500
  },

  peach: {
    id: 'peach',
    name: 'Princess Peach',
    origin: 'Mario Bros',
    homeworld: 'Mushroom Kingdom',
    description: 'Ruler of the Mushroom Kingdom. Often kidnapped by Bowser but capable of defending herself.',
    appearance: {
      ascii: '👸🍑',
      color: '#FFB6C1',
      description: 'Princess in pink dress with blonde hair and crown'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Benevolent royal ruler',
      aggression: 'Low'
    },
    dialogue: [
      'Thank you, Mario!',
      'I\'ll bake you a cake!',
      'The Mushroom Kingdom is safe!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 4000
  },

  // ============================================================================
  // SONIC UNIVERSE
  // ============================================================================

  badnik_motobug: {
    id: 'badnik_motobug',
    name: 'Motobug',
    origin: 'Sonic Universe',
    homeworld: 'Green Hill Zone',
    description: 'Small ladybug-like robots that patrol Green Hill Zone. Easy to destroy with a spin dash.',
    appearance: {
      ascii: '🤖🐞',
      color: '#FF0000',
      description: 'Red and black robotic ladybug with wheels'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Low',
      technology: 'Advanced',
      culture: 'Dr. Robotnik\'s automated army',
      aggression: 'Medium'
    },
    dialogue: [
      '*Mechanical beeping*',
      '*Whirring patrol sounds*',
      '*Alert! Intruder detected!*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 300
  },

  badnik_buzzer: {
    id: 'badnik_buzzer',
    name: 'Buzzer',
    origin: 'Sonic Universe',
    homeworld: 'Chemical Plant Zone',
    description: 'Wasp-like robots that fly in straight lines. They shoot projectiles when approached.',
    appearance: {
      ascii: '🤖🐝',
      color: '#FFD700',
      description: 'Yellow and black robotic wasp with stinger cannon'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Low',
      technology: 'Advanced',
      culture: 'Automated defense systems',
      aggression: 'High'
    },
    dialogue: [
      '*Buzzing mechanical sounds*',
      '*Targeting system activated*',
      '*Projectile launch sequence*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 400
  },

  chao_neutral: {
    id: 'chao_neutral',
    name: 'Neutral Chao',
    origin: 'Sonic Universe',
    homeworld: 'Chao Garden',
    description: 'Adorable creatures that live in Chao Gardens. They love to play and can evolve based on care.',
    appearance: {
      ascii: '💙',
      color: '#87CEEB',
      description: 'Small blue creature with large head and innocent eyes'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Primitive',
      culture: 'Innocent garden dwellers focused on play and growth',
      aggression: 'None'
    },
    dialogue: [
      'Chao chao!',
      '*Happy chirping sounds*',
      '*Playful giggling*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 2000
  },

  flicky_bird: {
    id: 'flicky_bird',
    name: 'Flicky',
    origin: 'Sonic Universe',
    homeworld: 'Green Hill Zone',
    description: 'Small blue birds that are often trapped inside badniks. Freeing them grants bonus points.',
    appearance: {
      ascii: '🐦',
      color: '#4169E1',
      description: 'Small blue bird with bright yellow beak'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Low',
      technology: 'Primitive',
      culture: 'Simple forest creatures seeking freedom',
      aggression: 'None'
    },
    dialogue: [
      '*Cheerful chirping*',
      '*Tweet tweet!*',
      '*Grateful bird song*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 100
  },

  // ============================================================================
  // ZELDA UNIVERSE
  // ============================================================================

  link: {
    id: 'link',
    name: 'Link',
    origin: 'Zelda Universe',
    homeworld: 'Hyrule Kingdom',
    description: 'The legendary Hero of Time, wielder of the Master Sword and protector of Hyrule.',
    appearance: {
      ascii: '⚔️🛡️',
      color: '#228B22',
      description: 'Green-clad hero with pointed ears and Master Sword'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Legendary heroes chosen by the goddesses',
      aggression: 'Low'
    },
    dialogue: [
      'Well met, traveler. The winds of adventure call.',
      'May courage light your path through the darkness.',
      'I cannot let evil pass unchallenged.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 5000
  },

  zelda: {
    id: 'zelda',
    name: 'Princess Zelda',
    origin: 'Zelda Universe',
    homeworld: 'Hyrule Kingdom',
    description: 'The wise princess of Hyrule, bearer of the Triforce of Wisdom.',
    appearance: {
      ascii: '👑✨',
      color: '#FFB6C1',
      description: 'Royal princess with golden hair and elegant dress'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Royal bloodline with divine wisdom',
      aggression: 'Very Low'
    },
    dialogue: [
      'Welcome to Hyrule, noble traveler.',
      'The goddesses smile upon those who seek justice.',
      'Wisdom guides my hand against those who threaten peace.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 4000
  },

  impa: {
    id: 'impa',
    name: 'Impa',
    origin: 'Zelda Universe',
    homeworld: 'Kakariko Village',
    description: 'The loyal Sheikah warrior and protector of the royal family.',
    appearance: {
      ascii: '🥷',
      color: '#4B0082',
      description: 'Mysterious warrior in dark clothing with red eyes'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Ancient shadow clan serving the royal family',
      aggression: 'Medium'
    },
    dialogue: [
      'The shadows reveal all truths.',
      'The Sheikah watch over those who walk in light.',
      'You threaten what I am sworn to protect.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 3500
  },

  epona: {
    id: 'epona',
    name: 'Epona',
    origin: 'Zelda Universe',
    homeworld: 'Lon Lon Ranch',
    description: 'Link\'s faithful steed, swift as the wind across Hyrule Field.',
    appearance: {
      ascii: '🐎',
      color: '#D2691E',
      description: 'Beautiful chestnut mare with flowing mane'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Primitive',
      culture: 'Noble steeds bonded to heroes',
      aggression: 'Very Low'
    },
    dialogue: [
      '*Neighs warmly in greeting*',
      '*Nuzzles affectionately*',
      '*Rears up defensively*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2000
  },

  navi: {
    id: 'navi',
    name: 'Navi',
    origin: 'Zelda Universe',
    homeworld: 'Lost Woods',
    description: 'A helpful fairy companion with knowledge of Hyrule\'s secrets.',
    appearance: {
      ascii: '🧚♀️',
      color: '#00FFFF',
      description: 'Glowing blue fairy with translucent wings'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Forest spirits serving as guides',
      aggression: 'None'
    },
    dialogue: [
      'Hey! Listen! I can help you on your journey!',
      'The forest spirits whisper of hidden treasures nearby.',
      'Watch out! Danger ahead!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1500
  },

  goron: {
    id: 'goron',
    name: 'Goron',
    origin: 'Zelda Universe',
    homeworld: 'Death Mountain',
    description: 'Rock-eating mountain dwellers known for their strength and craftsmanship.',
    appearance: {
      ascii: '🗿',
      color: '#8B4513',
      description: 'Large rocky humanoid with brown stone skin'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Average',
      culture: 'Mountain-dwelling craftsmen and miners',
      aggression: 'Low'
    },
    dialogue: [
      'Brother! Welcome to Death Mountain!',
      'Hot rocks and good food make for strong bonds!',
      'You dare threaten the Goron people?'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 3000
  },

  zora: {
    id: 'zora',
    name: 'Zora',
    origin: 'Zelda Universe',
    homeworld: 'Zora\'s Domain',
    description: 'Aquatic people who rule the rivers and seas with grace.',
    appearance: {
      ascii: '🧜♂️',
      color: '#00CED1',
      description: 'Blue-skinned aquatic humanoid with fins and gills'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Average',
      culture: 'Elegant water-dwelling civilization',
      aggression: 'Low'
    },
    dialogue: [
      'The waters flow peacefully when friends meet.',
      'May the currents carry you to safe harbors.',
      'The tide turns against those who bring harm.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 2800
  },

  rito: {
    id: 'rito',
    name: 'Rito',
    origin: 'Zelda Universe',
    homeworld: 'Rito Village',
    description: 'Bird-like people who soar through the skies with unmatched freedom.',
    appearance: {
      ascii: '🦅',
      color: '#4682B4',
      description: 'Humanoid bird with colorful feathers and wings'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Average',
      culture: 'Sky-dwelling nomads valuing freedom',
      aggression: 'Low'
    },
    dialogue: [
      'The winds carry news of your arrival, traveler.',
      'Fly high and let the wind guide your spirit.',
      'The sky itself will turn against you.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 2600
  },

  korok: {
    id: 'korok',
    name: 'Korok',
    origin: 'Zelda Universe',
    homeworld: 'Korok Forest',
    description: 'Forest spirits in wooden masks, guardians of nature\'s secrets.',
    appearance: {
      ascii: '🌿',
      color: '#228B22',
      description: 'Small wooden creature with leaf mask and twig arms'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Primitive',
      culture: 'Ancient forest spirits protecting nature',
      aggression: 'None'
    },
    dialogue: [
      'Yahaha! You found me!',
      'Here, take this! The forest provides for its friends.',
      'The forest does not welcome those with dark hearts.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 1800
  },

  ganondorf: {
    id: 'ganondorf',
    name: 'Ganondorf',
    origin: 'Zelda Universe',
    homeworld: 'Dark World',
    description: 'The King of Evil, wielder of the Triforce of Power and eternal enemy of Hyrule.',
    appearance: {
      ascii: '👹⚡',
      color: '#8B0000',
      description: 'Massive dark-skinned humanoid with fiery red hair and evil aura'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Ancient evil seeking ultimate power',
      aggression: 'Very High'
    },
    dialogue: [
      'Foolish mortal, you dare enter my domain?',
      'Power! The Triforce of Power is mine!',
      'Even darkness can appreciate strength when it sees it.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Legendary',
    tradeValue: 10000
  },

  bokoblin: {
    id: 'bokoblin',
    name: 'Bokoblin',
    origin: 'Zelda Universe',
    homeworld: 'Cursed Lands',
    description: 'Savage pig-like monsters that serve the forces of darkness.',
    appearance: {
      ascii: '🐷⚔️',
      color: '#8B4513',
      description: 'Pig-like humanoid with crude weapons and armor'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Low',
      technology: 'Primitive',
      culture: 'Chaotic monster tribes serving evil',
      aggression: 'High'
    },
    dialogue: [
      'Grraaah! Intruder!',
      'Bokoblin smash! Bokoblin destroy!',
      'Bokoblin... confused. You not enemy?'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 800
  },

  guardian: {
    id: 'guardian',
    name: 'Guardian',
    origin: 'Zelda Universe',
    homeworld: 'Cursed Lands',
    description: 'Ancient mechanical sentinels corrupted by malice, now hostile to all life.',
    appearance: {
      ascii: '🤖👁️',
      color: '#FF4500',
      description: 'Spider-like mechanical construct with glowing red eye'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Corrupted ancient defense systems',
      aggression: 'Very High'
    },
    dialogue: [
      '*Mechanical whirring and targeting sounds*',
      '*Charging laser with malicious intent*',
      '*Systems temporarily stabilized*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare',
    tradeValue: 6000
  },

  // ============================================================================
  // STAR WARS UNIVERSE
  // ============================================================================

  humancoruscant: {
    id: 'humancoruscant',
    name: 'Coruscant Human',
    origin: 'Star Wars',
    homeworld: 'Coruscant',
    description: 'Humans from the galactic capital. Sophisticated, politically savvy, and well-connected.',
    appearance: {
      ascii: '👤🏙️',
      color: '#4a5568',
      description: 'Well-dressed human from urban world'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Urban cosmopolitan',
      aggression: 'Low'
    },
    dialogue: [
      'Welcome to Coruscant, the center of the galaxy.',
      'I have connections in the Senate.',
      'The city-planet never sleeps.',
      'Credits talk louder than words here.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 500
  },

  jedi: {
    id: 'jedi',
    name: 'Jedi Knight',
    origin: 'Star Wars',
    homeworld: 'Various',
    description: 'Guardians of peace and justice. Wielders of the Force and lightsabers.',
    appearance: {
      ascii: '⚔️✨',
      color: '#3b82f6',
      description: 'Robed figure with lightsaber'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Monastic warriors',
      aggression: 'Very Low'
    },
    dialogue: [
      'May the Force be with you.',
      'There is no emotion, there is peace.',
      'Your feelings betray you.',
      'The Force will be with you, always.'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 0
  },

  sith: {
    id: 'sith',
    name: 'Sith Lord',
    origin: 'Star Wars',
    homeworld: 'Various',
    description: 'Dark side Force users. Seekers of power through passion and strength.',
    appearance: {
      ascii: '⚔️💀',
      color: '#dc2626',
      description: 'Dark-robed figure with red lightsaber'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Dark side order',
      aggression: 'High'
    },
    dialogue: [
      'You underestimate the power of the dark side.',
      'Your feeble skills are no match for the Force.',
      'I find your lack of faith disturbing.',
      'The dark side is a pathway to many abilities.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare',
    tradeValue: 0
  },

  jawa: {
    id: 'jawa',
    name: 'Jawa',
    origin: 'Star Wars',
    homeworld: 'Tatooine',
    description: 'Small scavengers in brown robes. Expert traders and droid mechanics.',
    appearance: {
      ascii: '👁️🔧',
      color: '#d97706',
      description: 'Small hooded figure with glowing eyes'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Average',
      technology: 'Industrial',
      culture: 'Scavenger traders',
      aggression: 'Very Low'
    },
    dialogue: [
      'Utinni!',
      'Houdini! Houdini!',
      'Taa baa!',
      '*Offers salvaged droid parts*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 300
  },

  tuskenraider: {
    id: 'tuskenraider',
    name: 'Tusken Raider',
    origin: 'Star Wars',
    homeworld: 'Tatooine',
    description: 'Sand People. Fierce desert warriors who attack travelers.',
    appearance: {
      ascii: '🏜️⚔️',
      color: '#a16207',
      description: 'Masked desert warrior with gaffi stick'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Average',
      technology: 'Primitive',
      culture: 'Nomadic warriors',
      aggression: 'High'
    },
    dialogue: [
      '*Aggressive grunting and war cries*',
      '*Raises gaffi stick threateningly*',
      '*Sand People always ride single file*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 0
  },

  hutt: {
    id: 'hutt',
    name: 'Hutt',
    origin: 'Star Wars',
    homeworld: 'Nal Hutta',
    description: 'Massive slug-like crime lords. Control vast criminal empires.',
    appearance: {
      ascii: '🐌👑',
      color: '#16a34a',
      description: 'Enormous slug-like being'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Crime syndicate',
      aggression: 'Medium'
    },
    dialogue: [
      'Ho ho ho ho!',
      'Bring me Solo and the Wookiee!',
      'You will pay for this outrage!',
      'I have a bad feeling about this deal.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2000
  },

  wampa: {
    id: 'wampa',
    name: 'Wampa',
    origin: 'Star Wars',
    homeworld: 'Hoth',
    description: 'Massive white-furred predator. Hunts in the frozen wastes of Hoth.',
    appearance: {
      ascii: '🐻❄️',
      color: '#e0f2fe',
      description: 'Large white predator with horns'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Low',
      technology: 'Primitive',
      culture: 'Predator',
      aggression: 'Very High'
    },
    dialogue: [
      '*Roars ferociously*',
      '*Charges with claws extended*',
      '*Hungry growling*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 0
  },

  tauntaun: {
    id: 'tauntaun',
    name: 'Tauntaun',
    origin: 'Star Wars',
    homeworld: 'Hoth',
    description: 'Reptomammal used as mounts on Hoth. Smells bad on the outside.',
    appearance: {
      ascii: '🦎🐴',
      color: '#94a3b8',
      description: 'Lizard-like mount creature'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Low',
      technology: 'Primitive',
      culture: 'Domesticated',
      aggression: 'None'
    },
    dialogue: [
      '*Bleats nervously*',
      '*Snorts and shakes head*',
      '*Friendly nuzzling*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 800
  },

  ewok: {
    id: 'ewok',
    name: 'Ewok',
    origin: 'Star Wars',
    homeworld: 'Endor',
    description: 'Small furry forest dwellers. Primitive but resourceful warriors.',
    appearance: {
      ascii: '🐻🌲',
      color: '#78350f',
      description: 'Small teddy bear-like creature'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Primitive',
      culture: 'Tribal forest dwellers',
      aggression: 'Low'
    },
    dialogue: [
      'Yub nub!',
      'Ee choya!',
      '*Offers roasted meat*',
      '*Drums on log rhythmically*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 400
  },

  gungan: {
    id: 'gungan',
    name: 'Gungan',
    origin: 'Star Wars',
    homeworld: 'Naboo',
    description: 'Amphibious species living in underwater cities. Meesa friendly!',
    appearance: {
      ascii: '🐸💧',
      color: '#f97316',
      description: 'Amphibious humanoid with long ears'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Advanced',
      culture: 'Underwater civilization',
      aggression: 'Low'
    },
    dialogue: [
      'Meesa Jar Jar Binks!',
      'Yousa in big doo-doo this time!',
      'How wude!',
      'Exsqueeze me!'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 500
  },

  humannaboo: {
    id: 'humannaboo',
    name: 'Naboo Human',
    origin: 'Star Wars',
    homeworld: 'Naboo',
    description: 'Peaceful humans from beautiful Naboo. Artistic and diplomatic.',
    appearance: {
      ascii: '👤🌊',
      color: '#3b82f6',
      description: 'Elegantly dressed human'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Artistic democracy',
      aggression: 'Very Low'
    },
    dialogue: [
      'Welcome to Naboo, jewel of the Mid Rim.',
      'Our queen values peace above all.',
      'The waterfalls of Theed are magnificent.',
      'May the Force guide your journey.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 600
  },

  yoda: {
    id: 'yoda',
    name: 'Yoda',
    origin: 'Star Wars',
    homeworld: 'Unknown',
    description: 'Ancient Jedi Master. Wise and powerful in the Force. Speaks in riddles.',
    appearance: {
      ascii: '👴✨',
      color: '#22c55e',
      description: 'Small green Jedi Master'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Godlike',
      technology: 'Advanced',
      culture: 'Jedi Master',
      aggression: 'Very Low'
    },
    dialogue: [
      'Do or do not. There is no try.',
      'Fear is the path to the dark side.',
      'Size matters not.',
      'Much to learn, you still have.'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 0
  },

  kaminoan: {
    id: 'kaminoan',
    name: 'Kaminoan',
    origin: 'Star Wars',
    homeworld: 'Kamino',
    description: 'Tall, graceful cloners. Masters of genetic engineering.',
    appearance: {
      ascii: '👽🧬',
      color: '#e0f2fe',
      description: 'Tall pale humanoid with elongated neck'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'Scientific cloners',
      aggression: 'Very Low'
    },
    dialogue: [
      'Your clones are very impressive.',
      'We modified their genetic structure.',
      'The first batch will be ready soon.',
      'Cloning is our specialty.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 3000
  },

  humanbespin: {
    id: 'humanbespin',
    name: 'Bespin Human',
    origin: 'Star Wars',
    homeworld: 'Bespin',
    description: 'Cloud City residents. Tibanna gas miners and traders.',
    appearance: {
      ascii: '👤☁️',
      color: '#f59e0b',
      description: 'Human from floating city'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Average',
      technology: 'Advanced',
      culture: 'Mining colony',
      aggression: 'Low'
    },
    dialogue: [
      'Welcome to Cloud City!',
      'Tibanna gas prices are up this cycle.',
      'This deal is getting worse all the time.',
      'The Empire has arrived.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 500
  },

  scavenger: {
    id: 'scavenger',
    name: 'Jakku Scavenger',
    origin: 'Star Wars',
    homeworld: 'Jakku',
    description: 'Desert scavenger picking through starship wreckage.',
    appearance: {
      ascii: '👤🔧',
      color: '#d97706',
      description: 'Wrapped in desert garb with salvage'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Average',
      technology: 'Industrial',
      culture: 'Scavenger',
      aggression: 'Low'
    },
    dialogue: [
      'One quarter portion.',
      'That\'s worth... sixty portions!',
      'The garbage will do.',
      '*Examines salvage carefully*'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 200
  },

  bountyhunter: {
    id: 'bountyhunter',
    name: 'Bounty Hunter',
    origin: 'Star Wars',
    homeworld: 'Various',
    description: 'Professional mercenary tracking targets across the galaxy.',
    appearance: {
      ascii: '🎯🔫',
      color: '#6b7280',
      description: 'Armored figure with weapons'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Mercenary',
      aggression: 'High'
    },
    dialogue: [
      'I\'m just a simple man trying to make my way in the universe.',
      'No disintegrations.',
      'He\'s worth a lot to me.',
      'I have you now.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 0
  },

  // ============================================================================
  // DOCTOR WHO UNIVERSE
  // ============================================================================

  timelord: {
    id: 'timelord',
    name: 'Time Lord',
    origin: 'Doctor Who',
    homeworld: 'Gallifrey',
    description: 'Ancient race with mastery over time. Can regenerate when near death.',
    appearance: {
      ascii: '⏰👤',
      color: '#d97706',
      description: 'Regal humanoid in ceremonial robes'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Godlike',
      technology: 'Superior',
      culture: 'Temporal civilization',
      aggression: 'Low'
    },
    dialogue: [
      'Time can be rewritten.',
      'The Laws of Time are mine to command.',
      'We are the Lords of Time.',
      'Gallifrey stands!'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 0
  },

  thedoctor: {
    id: 'thedoctor',
    name: 'The Doctor',
    origin: 'Doctor Who',
    homeworld: 'Gallifrey',
    description: 'Renegade Time Lord. Travels in the TARDIS saving civilizations.',
    appearance: {
      ascii: '🔷👤',
      color: '#3b82f6',
      description: 'Eccentric humanoid with sonic screwdriver'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Godlike',
      technology: 'Superior',
      culture: 'Time traveler',
      aggression: 'Very Low'
    },
    dialogue: [
      'Allons-y!',
      'Geronimo!',
      'I\'m the Doctor. Run.',
      'Wibbly-wobbly, timey-wimey stuff.'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 0
  },

  dalek: {
    id: 'dalek',
    name: 'Dalek',
    origin: 'Doctor Who',
    homeworld: 'Skaro',
    description: 'Mutant creatures in armored casings. Bent on universal domination.',
    appearance: {
      ascii: '🤖💀',
      color: '#78716c',
      description: 'Armored tank-like creature with eyestalk'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'Genocidal empire',
      aggression: 'Very High'
    },
    dialogue: [
      'EXTERMINATE! EXTERMINATE!',
      'YOU WILL BE EXTERMINATED!',
      'DALEKS CONQUER AND DESTROY!',
      'RESISTANCE IS FUTILE!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 0
  },

  cyberman: {
    id: 'cyberman',
    name: 'Cyberman',
    origin: 'Doctor Who',
    homeworld: 'Mondas',
    description: 'Converted humans with cybernetic implants. Emotionless and logical.',
    appearance: {
      ascii: '🤖❄️',
      color: '#3b82f6',
      description: 'Silver humanoid cyborg'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Superior',
      culture: 'Cybernetic collective',
      aggression: 'High'
    },
    dialogue: [
      'You will be upgraded.',
      'DELETE. DELETE.',
      'Resistance is illogical.',
      'You will become like us.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 0
  },

  sontaran: {
    id: 'sontaran',
    name: 'Sontaran',
    origin: 'Doctor Who',
    homeworld: 'Sontar',
    description: 'Clone warriors bred for battle. Short, stocky, and aggressive.',
    appearance: {
      ascii: '⚔️👽',
      color: '#16a34a',
      description: 'Stocky warrior with dome-shaped head'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Average',
      technology: 'Advanced',
      culture: 'Military clones',
      aggression: 'Very High'
    },
    dialogue: [
      'Sontar-ha!',
      'Glory to Sontar!',
      'I am a warrior of the Sontaran Empire!',
      'Death is but a moment!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 0
  },

  weepingangel: {
    id: 'weepingangel',
    name: 'Weeping Angel',
    origin: 'Doctor Who',
    homeworld: 'Unknown',
    description: 'Quantum-locked assassins. Move only when not observed. Don\'t blink!',
    appearance: {
      ascii: '👼💀',
      color: '#9ca3af',
      description: 'Stone angel statue'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Predatory',
      aggression: 'Very High'
    },
    dialogue: [
      '*Stone statue, unmoving*',
      '*Covers eyes when observed*',
      '*Moves when you blink*',
      'Don\'t blink. Don\'t even blink.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare',
    tradeValue: 0
  },

  silence: {
    id: 'silence',
    name: 'The Silence',
    origin: 'Doctor Who',
    homeworld: 'Unknown',
    description: 'Memory-proof aliens. You forget them the moment you look away.',
    appearance: {
      ascii: '👽🚫',
      color: '#1f2937',
      description: 'Tall grey humanoid in suit'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'Religious order',
      aggression: 'High'
    },
    dialogue: [
      'You should kill us all on sight.',
      'Silence will fall.',
      '*You forget this conversation*',
      '*Was someone just here?*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare',
    tradeValue: 0
  },

  vashtanerada: {
    id: 'vashtanerada',
    name: 'Vashta Nerada',
    origin: 'Doctor Who',
    homeworld: 'Unknown',
    description: 'Microscopic carnivores that live in shadows. Count the shadows.',
    appearance: {
      ascii: '🌑💀',
      color: '#000000',
      description: 'Living shadows'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Average',
      technology: 'Primitive',
      culture: 'Swarm predators',
      aggression: 'Very High'
    },
    dialogue: [
      'Hey, who turned out the lights?',
      '*Shadows moving independently*',
      'Donna Noble has left the library.',
      '*Extra shadow detected*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare',
    tradeValue: 0
  },

  ood: {
    id: 'ood',
    name: 'Ood',
    origin: 'Doctor Who',
    homeworld: 'Ood Sphere',
    description: 'Peaceful telepathic species. Gentle and kind. The song is beautiful.',
    appearance: {
      ascii: '🐙🎵',
      color: '#e0f2fe',
      description: 'Humanoid with tentacled face'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Average',
      technology: 'Advanced',
      culture: 'Telepathic collective',
      aggression: 'None'
    },
    dialogue: [
      'The song is ending.',
      'We are the Ood. We welcome you.',
      '*Telepathic harmony*',
      'The circle must be broken.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 1000
  },

  slitheen: {
    id: 'slitheen',
    name: 'Slitheen',
    origin: 'Doctor Who',
    homeworld: 'Raxacoricofallapatorius',
    description: 'Large green aliens who wear human skin suits. Criminal family.',
    appearance: {
      ascii: '👽💚',
      color: '#22c55e',
      description: 'Large green humanoid'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Criminal family',
      aggression: 'Medium'
    },
    dialogue: [
      '*Farts with compression field*',
      'Raxacoricofallapatorius!',
      'Humans are so primitive.',
      '*Unzips forehead*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 0
  },

  midnightentity: {
    id: 'midnightentity',
    name: 'Midnight Entity',
    origin: 'Doctor Who',
    homeworld: 'Midnight',
    description: 'Unknown entity that lives in Xtonic radiation. Repeats everything.',
    appearance: {
      ascii: '👁️💎',
      color: '#0ea5e9',
      description: 'Invisible presence'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Unknown',
      technology: 'Unknown',
      culture: 'Unknown',
      aggression: 'Very High'
    },
    dialogue: [
      '*Repeats your words exactly*',
      '*Speaks in perfect unison*',
      '*Takes control of speech*',
      'It\'s looking at you.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Legendary',
    tradeValue: 0
  },

  humannewearth: {
    id: 'humannewearth',
    name: 'New Earth Human',
    origin: 'Doctor Who',
    homeworld: 'New Earth',
    description: 'Far-future humans on New Earth. Advanced medical technology.',
    appearance: {
      ascii: '👤🌍',
      color: '#3b82f6',
      description: 'Human from far future'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Superior',
      culture: 'Medical advancement',
      aggression: 'Very Low'
    },
    dialogue: [
      'Welcome to New New York!',
      'The traffic is eternal.',
      'The Face of Boe watches over us.',
      'Humanity endures.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 800
  },

  faceofboe: {
    id: 'faceofboe',
    name: 'Face of Boe',
    origin: 'Doctor Who',
    homeworld: 'Unknown',
    description: 'Ancient being. Massive head in a jar. Knows great secrets.',
    appearance: {
      ascii: '🫙👤',
      color: '#8b5cf6',
      description: 'Enormous face in containment'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Godlike',
      technology: 'Superior',
      culture: 'Ancient being',
      aggression: 'None'
    },
    dialogue: [
      'You are not alone.',
      '*Telepathic wisdom*',
      'The secret must be told.',
      '*Ancient knowledge shared*'
    ],
    tradeable: false,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 0
  },

  // ============================================================================
  // ADDITIONAL STAR TREK SPECIES
  // ============================================================================

  humanstarfleet: {
    id: 'humanstarfleet',
    name: 'Starfleet Officer',
    origin: 'Star Trek',
    homeworld: 'Earth',
    description: 'Starfleet officers from the United Federation of Planets. Explorers and peacekeepers dedicated to seeking out new life and new civilizations.',
    appearance: {
      ascii: '👤⭐',
      color: '#3b82f6',
      description: 'Human in blue Starfleet uniform with comm badge'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Federation explorers',
      aggression: 'Very Low'
    },
    dialogue: [
      'Greetings! I\'m from the United Federation of Planets.',
      'We come in peace, seeking new life and new civilizations.',
      'The Prime Directive guides our actions.',
      'To boldly go where no one has gone before.',
      'Live long and prosper, friend.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 600
  },

  romulans: {
    id: 'romulans',
    name: 'Romulans',
    origin: 'Star Trek',
    homeworld: 'Romulus',
    description: 'Cunning warriors descended from Vulcans who rejected logic. Masters of espionage and cloaking technology.',
    appearance: {
      ascii: '🛡️👤',
      color: '#166534',
      description: 'Humanoid with pointed ears and green-tinted skin, often in military uniform'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Military empire with cloaking tech',
      aggression: 'High'
    },
    dialogue: [
      'The Romulan Star Empire will not be trifled with.',
      'Honor is in the eye of the beholder.',
      'Our cloaking device makes us invisible to your sensors.',
      'Jolan tru, stranger. What brings you to our space?',
      'The Tal Shiar sees all, knows all.'
    ],
    tradeable: true,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 800
  },


  bajorans: {
    id: 'bajorans',
    name: 'Bajorans',
    origin: 'Star Trek',
    homeworld: 'Bajor',
    description: 'Deeply spiritual people who endured decades of Cardassian occupation. Known for their faith in the Prophets and distinctive nose ridges.',
    appearance: {
      ascii: '🙏💜',
      color: '#8b5cf6',
      description: 'Humanoid with distinctive ridged nose and often wearing religious jewelry'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Religious and spiritual society',
      aggression: 'Low'
    },
    dialogue: [
      'May the Prophets guide your path.',
      'Bajor is free, and we will never be enslaved again.',
      'The Celestial Temple watches over us all.',
      'Walk with the Prophets, my child.',
      'Our faith sustained us through the darkest times.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 500
  },

  cardassians: {
    id: 'cardassians',
    name: 'Cardassians',
    origin: 'Star Trek',
    homeworld: 'Cardassia Prime',
    description: 'Military-focused reptilian humanoids with a history of occupation and authoritarianism. Known for their cunning and ruthless efficiency.',
    appearance: {
      ascii: '🦎⚔️',
      color: '#78716c',
      description: 'Grey-skinned reptilian humanoid with prominent neck ridges and spoon-shaped forehead'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Authoritarian military state',
      aggression: 'High'
    },
    dialogue: [
      'Cardassia will rise again to its former glory.',
      'Order and discipline are the foundations of civilization.',
      'The state\'s needs supersede individual desires.',
      'You underestimate Cardassian resolve.',
      'There are always... possibilities.'
    ],
    tradeable: true,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 750
  },

  betazoids: {
    id: 'betazoids',
    name: 'Betazoids',
    origin: 'Star Trek',
    homeworld: 'Betazed',
    description: 'Telepathic humanoids known for their empathic abilities and emotional openness. Many serve as counselors in Starfleet.',
    appearance: {
      ascii: '🧠💖',
      color: '#ec4899',
      description: 'Humanoid with dark eyes and often gentle, knowing expression'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Empathic and telepathic society',
      aggression: 'Very Low'
    },
    dialogue: [
      'I sense great emotion within you.',
      'Your thoughts are quite fascinating.',
      'Telepathy is a gift to be used with wisdom.',
      'I feel your pain as if it were my own.',
      'The mind is a beautiful and complex thing.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 900
  },

  // ============================================================================
  // MASS EFFECT UNIVERSE
  // ============================================================================

  asari: {
    id: 'asari',
    name: 'Asari',
    origin: 'Mass Effect',
    homeworld: 'Thessia',
    description: 'Mono-gender species with biotic abilities and long lifespans. Diplomats and warriors.',
    appearance: { ascii: '💙👽', color: '#8844ff', description: 'Blue-skinned humanoid with head tentacles' },
    traits: { temperament: 'Friendly', intelligence: 'Very High', technology: 'Advanced', culture: 'Diplomatic', aggression: 'Low' },
    dialogue: ['Embrace eternity!', 'The Asari have walked the stars for millennia.', 'By the Goddess...'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1200
  },

  turian: {
    id: 'turian',
    name: 'Turian',
    origin: 'Mass Effect',
    homeworld: 'Palaven',
    description: 'Military culture with strict hierarchy. Backbone of Citadel defense.',
    appearance: { ascii: '🛡️👽', color: '#888888', description: 'Metallic carapace with facial markings' },
    traits: { temperament: 'Neutral', intelligence: 'High', technology: 'Advanced', culture: 'Military', aggression: 'Medium' },
    dialogue: ['Spirits guide us.', 'Discipline is the soul of an army.', 'For Palaven!'],
    tradeable: true, hostile: false, rarity: 'Common', tradeValue: 800
  },

  salarian: {
    id: 'salarian',
    name: 'Salarian',
    origin: 'Mass Effect',
    homeworld: 'Sur\'Kesh',
    description: 'Fast-thinking scientists and spies. Short lifespan, photographic memory.',
    appearance: { ascii: '🧪👽', color: '#00ff88', description: 'Amphibian features with large eyes' },
    traits: { temperament: 'Friendly', intelligence: 'Genius', technology: 'Superior', culture: 'Scientific', aggression: 'Very Low' },
    dialogue: ['Lots of ways to help people. Sometimes heal patients, sometimes execute dangerous people.', 'Had to be me. Someone else might have gotten it wrong.'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1000
  },

  krogan: {
    id: 'krogan',
    name: 'Krogan',
    origin: 'Mass Effect',
    homeworld: 'Tuchanka',
    description: 'Warrior race afflicted by genophage. Tough, aggressive, long-lived.',
    appearance: { ascii: '💪👽', color: '#cc6600', description: 'Massive reptilian with armored hide' },
    traits: { temperament: 'Aggressive', intelligence: 'Average', technology: 'Industrial', culture: 'Warrior', aggression: 'Very High' },
    dialogue: ['I am Krogan!', 'Anyone who fights us is either stupid or on Saren\'s payroll.', 'Heh heh heh.'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 900
  },

  quarian: {
    id: 'quarian',
    name: 'Quarian',
    origin: 'Mass Effect',
    homeworld: 'Rannoch',
    description: 'Nomadic species in environmental suits. Creators of the Geth.',
    appearance: { ascii: '🔧👽', color: '#9966ff', description: 'Humanoid in full environmental suit' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Advanced', culture: 'Nomadic', aggression: 'Low' },
    dialogue: ['Keelah se\'lai.', 'The Migrant Fleet is my home.', 'Emergency induction port.'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 1100
  },

  geth: {
    id: 'geth',
    name: 'Geth',
    origin: 'Mass Effect',
    homeworld: 'Rannoch',
    description: 'Synthetic intelligence. Networked consciousness. Misunderstood.',
    appearance: { ascii: '🤖👁️', color: '#ff0000', description: 'Robotic platform with glowing optic' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Superior', culture: 'Synthetic', aggression: 'Medium' },
    dialogue: ['Does this unit have a soul?', 'We are Geth.', 'Consensus achieved.'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  hanar: {
    id: 'hanar',
    name: 'Hanar',
    origin: 'Mass Effect',
    homeworld: 'Kahje',
    description: 'Jellyfish-like species. Polite, religious, bioluminescent.',
    appearance: { ascii: '🪼✨', color: '#ff69b4', description: 'Floating jellyfish with tentacles' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Advanced', culture: 'Religious', aggression: 'None' },
    dialogue: ['This one greets you.', 'The Enkindlers guide us.', 'This one is honored.'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 1300
  },

  elcor: {
    id: 'elcor',
    name: 'Elcor',
    origin: 'Mass Effect',
    homeworld: 'Dekuuna',
    description: 'Large, slow-moving species. Preface statements with emotional context.',
    appearance: { ascii: '🐘💬', color: '#8b7355', description: 'Elephant-like quadruped' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Advanced', culture: 'Deliberate', aggression: 'Very Low' },
    dialogue: ['Enthusiastic greeting: Hello!', 'Sincere apology: I did not mean to offend.', 'Genuine concern: Are you well?'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 1200
  },

  volus: {
    id: 'volus',
    name: 'Volus',
    origin: 'Mass Effect',
    homeworld: 'Irune',
    description: 'Small species in pressure suits. Financial experts of the galaxy.',
    appearance: { ascii: '💰🔘', color: '#a0a0a0', description: 'Short rotund figure in environmental suit' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Advanced', culture: 'Mercantile', aggression: 'Very Low' },
    dialogue: ['Earth-clan, I need to talk business.', 'Credits are the lifeblood of civilization.', '*wheeze*'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 950
  },

  batarian: {
    id: 'batarian',
    name: 'Batarian',
    origin: 'Mass Effect',
    homeworld: 'Khar\'shan',
    description: 'Four-eyed species. Slavers and pirates. Hostile to humans.',
    appearance: { ascii: '👁️👁️👽', color: '#8b4513', description: 'Four-eyed humanoid with aggressive stance' },
    traits: { temperament: 'Hostile', intelligence: 'Average', technology: 'Advanced', culture: 'Slaver', aggression: 'Very High' },
    dialogue: ['Humans took our colonies!', 'You\'ll make a fine slave.', 'Death to humanity!'],
    tradeable: false, hostile: true, rarity: 'Uncommon', tradeValue: 0
  },

  collector: {
    id: 'collector',
    name: 'Collector',
    origin: 'Mass Effect',
    homeworld: 'Unknown',
    description: 'Reaper-modified Protheans. Abduct entire colonies.',
    appearance: { ascii: '👾🦗', color: '#ff8800', description: 'Insectoid with glowing eyes' },
    traits: { temperament: 'Hostile', intelligence: 'High', technology: 'Superior', culture: 'Hive Mind', aggression: 'Very High' },
    dialogue: ['ASSUMING DIRECT CONTROL.', 'You will be harvested.', '*insectoid chittering*'],
    tradeable: false, hostile: true, rarity: 'Very Rare', tradeValue: 0
  },

  reaper: {
    id: 'reaper',
    name: 'Reaper',
    origin: 'Mass Effect',
    homeworld: 'Dark Space',
    description: 'Ancient machines. Harvest advanced civilizations every 50,000 years.',
    appearance: { ascii: '🦑🤖', color: '#ff0000', description: 'Massive cuttlefish-like machine' },
    traits: { temperament: 'Hostile', intelligence: 'Godlike', technology: 'Superior', culture: 'Machine', aggression: 'Very High' },
    dialogue: ['YOU EXIST BECAUSE WE ALLOW IT.', 'Your extinction is inevitable.', 'We are eternal.'],
    tradeable: false, hostile: true, rarity: 'Legendary', tradeValue: 0
  },

  // ============================================================================
  // HALO UNIVERSE
  // ============================================================================

  spartan: {
    id: 'spartan',
    name: 'Spartan',
    origin: 'Halo',
    homeworld: 'Reach',
    description: 'Augmented super-soldiers. Humanity\'s greatest warriors. Spartans never die.',
    appearance: { ascii: '🎖️🛡️', color: '#00ff00', description: 'Armored warrior in MJOLNIR armor' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Advanced', culture: 'Military', aggression: 'High' },
    dialogue: ['Spartans never die, they\'re just missing in action.', 'Sir, finishing this fight.', 'I need a weapon.'],
    tradeable: false, hostile: false, rarity: 'Legendary', tradeValue: 0
  },

  elite: {
    id: 'elite',
    name: 'Sangheili (Elite)',
    origin: 'Halo',
    homeworld: 'Sanghelios',
    description: 'Honor-bound warriors. Former Covenant, now allies. "Were it so easy."',
    appearance: { ascii: '⚔️👽', color: '#9933ff', description: 'Tall reptilian warrior with mandibles' },
    traits: { temperament: 'Neutral', intelligence: 'High', technology: 'Advanced', culture: 'Warrior', aggression: 'High' },
    dialogue: ['Were it so easy.', 'Wort wort wort!', 'By the Prophets!'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1100
  },

  grunt: {
    id: 'grunt',
    name: 'Unggoy (Grunt)',
    origin: 'Halo',
    homeworld: 'Balaho',
    description: 'Small methane-breathers. Cannon fodder. Surprisingly dangerous in groups.',
    appearance: { ascii: '😱💨', color: '#00ccff', description: 'Short alien in breathing apparatus' },
    traits: { temperament: 'Neutral', intelligence: 'Low', technology: 'Industrial', culture: 'Subservient', aggression: 'Medium' },
    dialogue: ['Run away!', 'They killed Flipyap!', '*methane breathing*'],
    tradeable: true, hostile: true, rarity: 'Common', tradeValue: 400
  },

  unscmarine: {
    id: 'unscmarine',
    name: 'UNSC Marine',
    origin: 'Halo',
    homeworld: 'Earth',
    description: 'Human soldiers of the UNSC. Brave defenders against the Covenant.',
    appearance: { ascii: '🎖️🔫', color: '#0066cc', description: 'Armored human soldier' },
    traits: { temperament: 'Friendly', intelligence: 'Average', technology: 'Advanced', culture: 'Military', aggression: 'High' },
    dialogue: ['Oorah!', 'Contact! Covenant!', 'We\'ve got your back, Chief.'],
    tradeable: false, hostile: false, rarity: 'Common', tradeValue: 0
  },

  cortana: {
    id: 'cortana',
    name: 'Cortana',
    origin: 'Halo',
    homeworld: 'Reach (Created)',
    description: 'Smart AI. Master Chief\'s companion. "Don\'t make a girl a promise..."',
    appearance: { ascii: '💙🤖', color: '#00d4ff', description: 'Holographic female AI' },
    traits: { temperament: 'Friendly', intelligence: 'Godlike', technology: 'Superior', culture: 'AI', aggression: 'Low' },
    dialogue: ['Don\'t make a girl a promise you can\'t keep.', 'I\'m your shield. I\'m your sword.', 'Welcome to Halo.'],
    tradeable: false, hostile: false, rarity: 'Legendary', tradeValue: 0
  },

  flood: {
    id: 'flood',
    name: 'Flood',
    origin: 'Halo',
    homeworld: 'Unknown',
    description: 'Parasitic horror. Consumes all sentient life. Gravemind intelligence.',
    appearance: { ascii: '🦠👾', color: '#00ff00', description: 'Grotesque infected biomass' },
    traits: { temperament: 'Hostile', intelligence: 'Very High', technology: 'Parasitic', culture: 'Hive Mind', aggression: 'Very High' },
    dialogue: ['*gurgling sounds*', 'We are the Flood. There is no difference.', 'Consumption is inevitable.'],
    tradeable: false, hostile: true, rarity: 'Very Rare', tradeValue: 0
  },

  forerunner: {
    id: 'forerunner',
    name: 'Forerunner',
    origin: 'Halo',
    homeworld: 'Unknown',
    description: 'Ancient builders of the Halo rings. Extinct but technology remains.',
    appearance: { ascii: '🏛️👽', color: '#ffaa00', description: 'Tall armored figure with advanced tech' },
    traits: { temperament: 'Neutral', intelligence: 'Godlike', technology: 'Superior', culture: 'Ancient', aggression: 'Medium' },
    dialogue: ['The Mantle of Responsibility is ours.', 'You are Forerunner.', 'Reclaimer...'],
    tradeable: false, hostile: false, rarity: 'Legendary', tradeValue: 0
  },

  sentinel: {
    id: 'sentinel',
    name: 'Sentinel',
    origin: 'Halo',
    homeworld: 'Halo Installations',
    description: 'Forerunner defense drones. Protect installations from intruders.',
    appearance: { ascii: '🔷🤖', color: '#00ff88', description: 'Floating robotic construct' },
    traits: { temperament: 'Hostile', intelligence: 'Average', technology: 'Superior', culture: 'Synthetic', aggression: 'High' },
    dialogue: ['*mechanical whirring*', 'Intruder detected.', '*laser charging*'],
    tradeable: false, hostile: true, rarity: 'Uncommon', tradeValue: 0
  },

  promethean: {
    id: 'promethean',
    name: 'Promethean',
    origin: 'Halo',
    homeworld: 'Requiem',
    description: 'Forerunner war machines. Digitized warriors. Serve the Didact.',
    appearance: { ascii: '⚡🤖', color: '#ff8800', description: 'Angular robotic warrior' },
    traits: { temperament: 'Hostile', intelligence: 'High', technology: 'Superior', culture: 'Synthetic', aggression: 'Very High' },
    dialogue: ['*digital screaming*', 'For the Didact!', '*teleportation sound*'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  didact: {
    id: 'didact',
    name: 'Didact',
    origin: 'Halo',
    homeworld: 'Requiem',
    description: 'Forerunner warrior. Imprisoned for millennia. Hates humanity.',
    appearance: { ascii: '👑👽', color: '#ff4400', description: 'Towering armored Forerunner' },
    traits: { temperament: 'Hostile', intelligence: 'Godlike', technology: 'Superior', culture: 'Ancient', aggression: 'Very High' },
    dialogue: ['So fades the great harvest of my betrayal.', 'You are Forerunner! But this ring is mine!', 'Humanity will burn.'],
    tradeable: false, hostile: true, rarity: 'Legendary', tradeValue: 0
  },

  covenant: {
    id: 'covenant',
    name: 'Covenant',
    origin: 'Halo',
    homeworld: 'High Charity',
    description: 'Religious alliance of alien species. Seek the Great Journey.',
    appearance: { ascii: '🛐👽', color: '#9933ff', description: 'Various alien species united' },
    traits: { temperament: 'Hostile', intelligence: 'High', technology: 'Advanced', culture: 'Religious', aggression: 'Very High' },
    dialogue: ['The Great Journey awaits!', 'Heretic!', 'For the Prophets!'],
    tradeable: false, hostile: true, rarity: 'Common', tradeValue: 0
  },

  jackal: {
    id: 'jackal',
    name: 'Kig-Yar (Jackal)',
    origin: 'Halo',
    homeworld: 'Eayn',
    description: 'Avian species serving as snipers and scouts in the Covenant. Cowardly but deadly accurate.',
    appearance: {
      ascii: '🦅🔫',
      color: '#8B4513',
      description: 'Bird-like alien with energy shield and beam rifle'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Average',
      technology: 'Advanced',
      culture: 'Mercenary snipers and pirates',
      aggression: 'Medium'
    },
    dialogue: [
      '*alien screeching*',
      'For profit and glory!',
      '*cowardly retreat sounds*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 600
  },

  prophet: {
    id: 'prophet',
    name: 'San\'Shyuum (Prophet)',
    origin: 'Halo',
    homeworld: 'Janjur Qom',
    description: 'Religious leaders of the Covenant. Frail but manipulative. Seek the Great Journey.',
    appearance: {
      ascii: '🛐👴',
      color: '#9370DB',
      description: 'Elderly alien in ornate robes on floating throne'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Religious hierarchy and manipulation',
      aggression: 'High'
    },
    dialogue: [
      'The Great Journey shall begin!',
      'Heresy will not be tolerated!',
      'The Forerunners have blessed us!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare',
    tradeValue: 1500
  },

  brute: {
    id: 'brute',
    name: 'Jiralhanae (Brute)',
    origin: 'Halo',
    homeworld: 'Doisac',
    description: 'Savage warriors. Replaced Elites in Covenant hierarchy.',
    appearance: { ascii: '🦍⚔️', color: '#ff4400', description: 'Massive ape-like warrior' },
    traits: { temperament: 'Hostile', intelligence: 'Average', technology: 'Industrial', culture: 'Brutal', aggression: 'Very High' },
    dialogue: ['*roaring*', 'Die, human!', 'For the pack!'],
    tradeable: false, hostile: true, rarity: 'Uncommon', tradeValue: 0
  },

  // ============================================================================
  // WARHAMMER 40K UNIVERSE
  // ============================================================================

  spacemarine: {
    id: 'spacemarine',
    name: 'Space Marine',
    origin: 'Warhammer 40K',
    homeworld: 'Various Chapter Worlds',
    description: 'Genetically enhanced super-soldiers. The Emperor\'s finest. They shall know no fear.',
    appearance: { ascii: '⚔️🛡️', color: '#0066ff', description: 'Massive armored warrior' },
    traits: { temperament: 'Neutral', intelligence: 'High', technology: 'Advanced', culture: 'Military', aggression: 'Very High' },
    dialogue: ['For the Emperor!', 'In the Emperor\'s name!', 'Death to the heretics!'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  ork: {
    id: 'ork',
    name: 'Ork',
    origin: 'Warhammer 40K',
    homeworld: 'Various',
    description: 'Green-skinned fungal warriors. Live for war. WAAAGH!',
    appearance: { ascii: '🟢⚔️', color: '#00ff00', description: 'Massive green brute' },
    traits: { temperament: 'Hostile', intelligence: 'Low', technology: 'Primitive', culture: 'Warrior', aggression: 'Very High' },
    dialogue: ['WAAAGH!', 'Needz more dakka!', 'Oi! You lookin\' fer a fight?'],
    tradeable: false, hostile: true, rarity: 'Common', tradeValue: 0
  },

  eldar: {
    id: 'eldar',
    name: 'Eldar',
    origin: 'Warhammer 40K',
    homeworld: 'Craftworlds',
    description: 'Ancient psychic race. Dying civilization. Masters of the Webway.',
    appearance: { ascii: '✨👽', color: '#ffaa00', description: 'Graceful alien with pointed features' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Superior', culture: 'Ancient', aggression: 'Medium' },
    dialogue: ['The Mon-Keigh are beneath us.', 'Fate guides our path.', 'For Isha!'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 1500
  },

  tau: {
    id: 'tau',
    name: 'T\'au',
    origin: 'Warhammer 40K',
    homeworld: 'T\'au',
    description: 'Young race with advanced technology. Believe in the Greater Good.',
    appearance: { ascii: '🔵🤖', color: '#0088cc', description: 'Blue-skinned humanoid with advanced tech' },
    traits: { temperament: 'Friendly', intelligence: 'Very High', technology: 'Superior', culture: 'Idealistic', aggression: 'Low' },
    dialogue: ['For the Greater Good!', 'Join us, and prosper.', 'The Ethereals guide us.'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1200
  },

  necron: {
    id: 'necron',
    name: 'Necron',
    origin: 'Warhammer 40K',
    homeworld: 'Tomb Worlds',
    description: 'Ancient robotic warriors. Awakening from millions of years of sleep.',
    appearance: { ascii: '💀🤖', color: '#00ff00', description: 'Skeletal robot with green glow' },
    traits: { temperament: 'Hostile', intelligence: 'Very High', technology: 'Superior', culture: 'Ancient', aggression: 'Very High' },
    dialogue: ['*mechanical whirring*', 'You will be harvested.', 'The galaxy is ours.'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  tyranid: {
    id: 'tyranid',
    name: 'Tyranid',
    origin: 'Warhammer 40K',
    homeworld: 'Unknown',
    description: 'Extragalactic swarm. Consume all biomass. Hive Mind intelligence.',
    appearance: { ascii: '🦗👾', color: '#9933ff', description: 'Chitinous alien horror' },
    traits: { temperament: 'Hostile', intelligence: 'Very High', technology: 'Biological', culture: 'Hive Mind', aggression: 'Very High' },
    dialogue: ['*chittering*', '*hive mind whispers*', '*alien screeching*'],
    tradeable: false, hostile: true, rarity: 'Very Rare', tradeValue: 0
  },

  chaosmarine: {
    id: 'chaosmarine',
    name: 'Chaos Space Marine',
    origin: 'Warhammer 40K',
    homeworld: 'Eye of Terror',
    description: 'Corrupted Space Marines. Serve the Chaos Gods. Traitors to the Imperium.',
    appearance: { ascii: '😈⚔️', color: '#ff0000', description: 'Corrupted armored warrior' },
    traits: { temperament: 'Hostile', intelligence: 'High', technology: 'Advanced', culture: 'Chaos', aggression: 'Very High' },
    dialogue: ['Death to the False Emperor!', 'Blood for the Blood God!', 'Chaos reigns!'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  daemon: {
    id: 'daemon',
    name: 'Daemon',
    origin: 'Warhammer 40K',
    homeworld: 'The Warp',
    description: 'Entities of pure Chaos. Manifestations of the Warp. Corruption incarnate.',
    appearance: { ascii: '👹🔥', color: '#ff00ff', description: 'Horrific warp entity' },
    traits: { temperament: 'Hostile', intelligence: 'Very High', technology: 'Warp', culture: 'Chaos', aggression: 'Very High' },
    dialogue: ['Your soul is ours!', 'The Warp hungers!', '*daemonic laughter*'],
    tradeable: false, hostile: true, rarity: 'Very Rare', tradeValue: 0
  },

  imperialguard: {
    id: 'imperialguard',
    name: 'Imperial Guard',
    origin: 'Warhammer 40K',
    homeworld: 'Various',
    description: 'Countless human soldiers. The hammer of the Emperor. Courage and steel.',
    appearance: { ascii: '🎖️🔫', color: '#666666', description: 'Human soldier in flak armor' },
    traits: { temperament: 'Friendly', intelligence: 'Average', technology: 'Industrial', culture: 'Military', aggression: 'High' },
    dialogue: ['For the Emperor!', 'Fix bayonets!', 'The Emperor protects!'],
    tradeable: false, hostile: false, rarity: 'Common', tradeValue: 0
  },

  inquisitor: {
    id: 'inquisitor',
    name: 'Inquisitor',
    origin: 'Warhammer 40K',
    homeworld: 'Terra',
    description: 'Agents of the Emperor. Hunt heretics, mutants, and daemons. Absolute authority.',
    appearance: { ascii: '🔍⚖️', color: '#ff0000', description: 'Armored figure with Inquisitorial seal' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Advanced', culture: 'Inquisition', aggression: 'High' },
    dialogue: ['Innocence proves nothing.', 'Burn the heretic!', 'The Emperor\'s will be done.'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  techpriest: {
    id: 'techpriest',
    name: 'Tech-Priest',
    origin: 'Warhammer 40K',
    homeworld: 'Mars',
    description: 'Adeptus Mechanicus. Worship the Machine God. Cybernetic enhancements.',
    appearance: { ascii: '⚙️🤖', color: '#ff4400', description: 'Robed figure with mechanical augments' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Advanced', culture: 'Mechanicus', aggression: 'Low' },
    dialogue: ['Praise the Omnissiah!', 'The machine spirit is pleased.', '*binary cant*'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1300
  },

  darkeldar: {
    id: 'darkeldar',
    name: 'Dark Eldar',
    origin: 'Warhammer 40K',
    homeworld: 'Commorragh',
    description: 'Sadistic raiders from the Webway. Feed on suffering. Masters of torture.',
    appearance: { ascii: '😈🗡️', color: '#330033', description: 'Sinister spiked armor' },
    traits: { temperament: 'Hostile', intelligence: 'Very High', technology: 'Superior', culture: 'Sadistic', aggression: 'Very High' },
    dialogue: ['Your pain sustains us.', 'Scream for me!', 'The Dark City hungers.'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  skitarii: {
    id: 'skitarii',
    name: 'Skitarii',
    origin: 'Warhammer 40,000',
    homeworld: 'Mars',
    description: 'Cybernetic soldiers of the Adeptus Mechanicus. Enhanced with sacred technology and blessed augmentations.',
    appearance: {
      ascii: '⚙️🔫',
      color: '#cc4400',
      description: 'Red-robed cyborg with extensive mechanical augmentations'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Tech-cult',
      aggression: 'High'
    },
    dialogue: [
      'The Omnissiah guides our aim.',
      'Flesh is weak, steel is eternal.',
      'Binary cant: 01001000 01100001 01101001 01101100',
      'Sacred protocols engaged.',
      'The machine spirit is willing.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1400
  },

  ultramarine: {
    id: 'ultramarine',
    name: 'Ultramarine',
    origin: 'Warhammer 40,000',
    homeworld: 'Macragge',
    description: 'Noble Space Marines of Ultramar. Masters of tactics and the Codex Astartes. Sons of Guilliman.',
    appearance: {
      ascii: '🔵⚔️',
      color: '#0066cc',
      description: 'Blue power armor with golden trim and Ultramarines insignia'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Honor-bound',
      aggression: 'Medium'
    },
    dialogue: [
      'Courage and honor!',
      'For Macragge and the Emperor!',
      'The Codex Astartes does not support this action.',
      'We march for Macragge!',
      'Theoretical: Practical.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2000
  },

  spacewolf: {
    id: 'spacewolf',
    name: 'Space Wolf',
    origin: 'Warhammer 40,000',
    homeworld: 'Fenris',
    description: 'Fierce warriors from the ice world of Fenris. Sons of Russ. The Emperor\'s executioners.',
    appearance: {
      ascii: '🐺⚔️',
      color: '#666666',
      description: 'Grey power armor with wolf pelts and Nordic runes'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Viking-like',
      aggression: 'High'
    },
    dialogue: [
      'For Russ and the Allfather!',
      'The wolf time comes.',
      'Fenris hjolda!',
      'By the beard of Russ!',
      'The hunt begins.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 1900
  },

  bloodangel: {
    id: 'bloodangel',
    name: 'Blood Angel',
    origin: 'Warhammer 40,000',
    homeworld: 'Baal',
    description: 'Noble but cursed Space Marines. Afflicted by the Red Thirst and Black Rage. Sons of Sanguinius.',
    appearance: {
      ascii: '🩸👼',
      color: '#cc0000',
      description: 'Red power armor with angelic wings and blood drop insignia'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Artistic warriors',
      aggression: 'High'
    },
    dialogue: [
      'By the blood of Sanguinius!',
      'For the Angel and the Emperor!',
      'The Red Thirst calls...',
      'Death! Death!',
      'Only in death does duty end.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 1950
  },

  haemonculus: {
    id: 'haemonculus',
    name: 'Haemonculus',
    origin: 'Warhammer 40,000',
    homeworld: 'Commorragh',
    description: 'Dark Eldar flesh-sculptors and torturers. Masters of pain and biological manipulation.',
    appearance: {
      ascii: '🧪💀',
      color: '#330066',
      description: 'Grotesquely modified Dark Eldar with surgical implements'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'Sadistic science',
      aggression: 'Very High'
    },
    dialogue: [
      'Pain is the purest form of art.',
      'Your flesh will serve my experiments.',
      'Scream louder, the symphony is incomplete.',
      'I shall remake you in agony.',
      'The Dark City appreciates fine craftsmanship.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare',
    tradeValue: 0
  },

  farseer: {
    id: 'farseer',
    name: 'Farseer',
    origin: 'Warhammer 40,000',
    homeworld: 'Craftworld',
    description: 'Eldar psyker who has walked the Path of the Seer. Sees possible futures through the warp.',
    appearance: {
      ascii: '🔮👁️',
      color: '#ffaa00',
      description: 'Tall Eldar in flowing robes with psychic third eye'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'Godlike',
      technology: 'Superior',
      culture: 'Psychic seers',
      aggression: 'Low'
    },
    dialogue: [
      'The skeins of fate are tangled.',
      'I have seen your death in a thousand futures.',
      'The Path of the Seer is perilous.',
      'Isha weeps for what must come to pass.',
      'The future is not set in stone, mon-keigh.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Very Rare',
    tradeValue: 2500
  },

  firewarrior: {
    id: 'firewarrior',
    name: 'Fire Warrior',
    origin: 'Warhammer 40,000',
    homeworld: 'T\'au',
    description: 'T\'au infantry soldiers armed with pulse rifles. Disciplined warriors of the Greater Good.',
    appearance: {
      ascii: '🔥🎯',
      color: '#0088cc',
      description: 'Blue-grey armor with advanced targeting systems'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Superior',
      culture: 'Greater Good',
      aggression: 'Medium'
    },
    dialogue: [
      'For the Greater Good!',
      'The Tau\'va guides us.',
      'Unity through diversity.',
      'Our technology will prevail.',
      'The Fire Caste stands ready.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 1100
  },

  ethereal: {
    id: 'ethereal',
    name: 'Ethereal',
    origin: 'Warhammer 40,000',
    homeworld: 'T\'au',
    description: 'Mysterious T\'au leaders who unite the castes. Possess an aura of natural authority.',
    appearance: {
      ascii: '👑🕊️',
      color: '#ffffff',
      description: 'Pale T\'au in ceremonial robes with caste markings'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'Philosophical leaders',
      aggression: 'Very Low'
    },
    dialogue: [
      'The Greater Good encompasses all.',
      'Unity is strength, division is weakness.',
      'The Tau\'va shall light the way.',
      'All castes serve the greater purpose.',
      'Through cooperation, we achieve the impossible.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2200
  },

  necronlord: {
    id: 'necronlord',
    name: 'Necron Lord',
    origin: 'Warhammer 40,000',
    homeworld: 'Tomb World',
    description: 'Ancient Necron nobility awakened from the Great Sleep. Commands legions of undying warriors.',
    appearance: {
      ascii: '👑💀',
      color: '#00ff00',
      description: 'Ornate skeletal robot with glowing green eyes and regal bearing'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'Undying dynasty',
      aggression: 'Very High'
    },
    dialogue: [
      'I have slept for sixty million years.',
      'The galaxy belongs to the Necrons.',
      'Your species is but a fleeting moment.',
      'Death is but a doorway.',
      'We are inevitable.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare',
    tradeValue: 0
  },

  chaoslord: {
    id: 'chaoslord',
    name: 'Chaos Lord',
    origin: 'Warhammer 40,000',
    homeworld: 'Eye of Terror',
    description: 'Corrupted Space Marine champion of the Dark Gods. Leads warbands in the Long War.',
    appearance: {
      ascii: '😈👑',
      color: '#990000',
      description: 'Corrupted power armor with daemonic mutations and dark trophies'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Chaos worship',
      aggression: 'Very High'
    },
    dialogue: [
      'Death to the False Emperor!',
      'The Dark Gods favor the bold.',
      'Chaos is the only truth.',
      'Let the galaxy burn!',
      'The Long War continues.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Very Rare',
    tradeValue: 0
  },

  // ============================================================================
  // MARVEL UNIVERSE
  // ============================================================================

  spiderman: {
    id: 'spiderman',
    name: 'Spider-Man',
    origin: 'Marvel',
    homeworld: 'Earth-616',
    description: 'Friendly neighborhood Spider-Man. With great power comes great responsibility.',
    appearance: { ascii: '🕷️🕸️', color: '#ff0000', description: 'Red and blue costume with web pattern' },
    traits: { temperament: 'Friendly', intelligence: 'Very High', technology: 'Advanced', culture: 'Hero', aggression: 'Low' },
    dialogue: ['With great power comes great responsibility.', 'Your friendly neighborhood Spider-Man!', '*web-slinging sounds*'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  ironman: {
    id: 'ironman',
    name: 'Iron Man',
    origin: 'Marvel',
    homeworld: 'Earth-616',
    description: 'Genius billionaire playboy philanthropist. Tony Stark in powered armor.',
    appearance: { ascii: '🤖💰', color: '#ff0000', description: 'Red and gold powered armor' },
    traits: { temperament: 'Friendly', intelligence: 'Genius', technology: 'Superior', culture: 'Hero', aggression: 'Medium' },
    dialogue: ['I am Iron Man.', 'Sometimes you gotta run before you can walk.', 'Jarvis, fire up the suit.'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  captainamerica: {
    id: 'captainamerica',
    name: 'Captain America',
    origin: 'Marvel',
    homeworld: 'Earth-616',
    description: 'Super-soldier. Symbol of freedom. "I can do this all day."',
    appearance: { ascii: '🛡️⭐', color: '#0044cc', description: 'Star-spangled uniform with shield' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Advanced', culture: 'Hero', aggression: 'Medium' },
    dialogue: ['I can do this all day.', 'Avengers, assemble!', 'I\'m just a kid from Brooklyn.'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  thor: {
    id: 'thor',
    name: 'Thor',
    origin: 'Marvel',
    homeworld: 'Asgard',
    description: 'God of Thunder. Wields Mjolnir. Son of Odin.',
    appearance: { ascii: '⚡🔨', color: '#ffaa00', description: 'Asgardian armor with hammer' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Magic', culture: 'Asgardian', aggression: 'High' },
    dialogue: ['I am Thor, son of Odin!', 'Bring me Thanos!', 'Another!'],
    tradeable: false, hostile: false, rarity: 'Legendary', tradeValue: 0
  },

  hulk: {
    id: 'hulk',
    name: 'Hulk',
    origin: 'Marvel',
    homeworld: 'Earth-616',
    description: 'The strongest there is. Bruce Banner\'s rage incarnate.',
    appearance: { ascii: '💪💚', color: '#00ff00', description: 'Massive green muscular figure' },
    traits: { temperament: 'Aggressive', intelligence: 'Average', technology: 'None', culture: 'Hero', aggression: 'Very High' },
    dialogue: ['HULK SMASH!', 'Hulk strongest there is!', '*roaring*'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  blackwidow: {
    id: 'blackwidow',
    name: 'Black Widow',
    origin: 'Marvel',
    homeworld: 'Earth-616',
    description: 'Master spy and assassin. Natasha Romanoff. Red in her ledger.',
    appearance: { ascii: '🕷️🔫', color: '#000000', description: 'Black tactical suit' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Advanced', culture: 'Spy', aggression: 'High' },
    dialogue: ['I\'ve got red in my ledger.', 'I\'m always picking up after you boys.', 'This is gonna work, Steve.'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  thanos: {
    id: 'thanos',
    name: 'Thanos',
    origin: 'Marvel',
    homeworld: 'Titan',
    description: 'The Mad Titan. Seeks the Infinity Stones. Inevitable.',
    appearance: { ascii: '💜👊', color: '#9933ff', description: 'Purple titan with golden gauntlet' },
    traits: { temperament: 'Hostile', intelligence: 'Genius', technology: 'Superior', culture: 'Conqueror', aggression: 'Very High' },
    dialogue: ['I am inevitable.', 'Perfectly balanced, as all things should be.', 'You should have gone for the head.'],
    tradeable: false, hostile: true, rarity: 'Legendary', tradeValue: 0
  },

  loki: {
    id: 'loki',
    name: 'Loki',
    origin: 'Marvel',
    homeworld: 'Asgard',
    description: 'God of Mischief. Trickster. Burdened with glorious purpose.',
    appearance: { ascii: '😈🗡️', color: '#00ff00', description: 'Asgardian with horned helmet' },
    traits: { temperament: 'Neutral', intelligence: 'Genius', technology: 'Magic', culture: 'Trickster', aggression: 'Medium' },
    dialogue: ['I am burdened with glorious purpose.', 'Kneel before me!', 'I assure you, brother, the sun will shine on us again.'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  groot: {
    id: 'groot',
    name: 'Groot',
    origin: 'Marvel',
    homeworld: 'Planet X',
    description: 'Flora colossus. Guardian of the Galaxy. "I am Groot."',
    appearance: { ascii: '🌳😊', color: '#8b4513', description: 'Tree-like humanoid' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'None', culture: 'Guardian', aggression: 'Low' },
    dialogue: ['I am Groot.', 'I am Groot!', 'I am Groot?'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  rocket: {
    id: 'rocket',
    name: 'Rocket Raccoon',
    origin: 'Marvel',
    homeworld: 'Halfworld',
    description: 'Genetically enhanced raccoon. Expert marksman and pilot. Ain\'t no thing like me.',
    appearance: { ascii: '🦝🔫', color: '#8b4513', description: 'Anthropomorphic raccoon with weapons' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Advanced', culture: 'Guardian', aggression: 'High' },
    dialogue: ['Ain\'t no thing like me, except me!', 'I live for the simple things... like how much this is gonna hurt!', 'Oh yeah!'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  hawkeye: {
    id: 'hawkeye',
    name: 'Hawkeye',
    origin: 'Marvel Comics',
    homeworld: 'Earth-616',
    description: 'Master archer and Avenger. Clint Barton. Never misses.',
    appearance: { ascii: '🏹🎯', color: '#9333ea', description: 'Purple and black tactical suit with bow' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Advanced', culture: 'Hero', aggression: 'Medium' },
    dialogue: ['Nobody would know. Nobody. Last I saw him, Ultron was sitting on him.', 'The city is flying and we\'re fighting an army of robots.', 'I retire for like, what, five minutes, and it all goes to shit.'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1300
  },

  heimdall: {
    id: 'heimdall',
    name: 'Heimdall',
    origin: 'Marvel Comics',
    homeworld: 'Asgard',
    description: 'All-seeing guardian of the Bifrost. Protector of Asgard.',
    appearance: { ascii: '👁️⚔️', color: '#fbbf24', description: 'Golden armor with glowing eyes' },
    traits: { temperament: 'Friendly', intelligence: 'Very High', technology: 'Magic', culture: 'Asgardian', aggression: 'Medium' },
    dialogue: ['I see you.', 'From here, I can see nine realms and ten trillion souls.', 'Your ancestors called it magic. You call it science.'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 2100
  },

  valkyrie: {
    id: 'valkyrie',
    name: 'Valkyrie',
    origin: 'Marvel Comics',
    homeworld: 'Asgard',
    description: 'Elite Asgardian warrior. Last of the Valkyries. King of New Asgard.',
    appearance: { ascii: '⚔️👑', color: '#3b82f6', description: 'Blue and white armor with sword' },
    traits: { temperament: 'Neutral', intelligence: 'High', technology: 'Magic', culture: 'Warrior', aggression: 'High' },
    dialogue: ['I\'m a Valkyrie. I\'ve spent years in a haze trying to forget my past.', 'He\'s a friend from work!', 'Welcome to New Asgard.'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 1900
  },

  nova: {
    id: 'nova',
    name: 'Nova Corps',
    origin: 'Marvel Comics',
    homeworld: 'Xandar',
    description: 'Intergalactic police force. Protectors of Xandar and the galaxy.',
    appearance: { ascii: '⭐🚀', color: '#fbbf24', description: 'Gold and blue uniform with helmet' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Superior', culture: 'Law enforcement', aggression: 'Medium' },
    dialogue: ['Nova Corps. You\'re under arrest.', 'For the glory of Xandar!', 'The Nova Force protects us all.'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1400
  },

  starlord: {
    id: 'starlord',
    name: 'Star-Lord',
    origin: 'Marvel Comics',
    homeworld: 'Earth-616',
    description: 'Peter Quill. Leader of the Guardians. Legendary outlaw.',
    appearance: { ascii: '🎵🚀', color: '#dc2626', description: 'Red leather jacket with helmet' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Advanced', culture: 'Guardian', aggression: 'Medium' },
    dialogue: ['I\'m Star-Lord, man. Legendary outlaw?', 'Dance-off, bro!', 'We are Groot.'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 1800
  },

  gamora: {
    id: 'gamora',
    name: 'Gamora',
    origin: 'Marvel Comics',
    homeworld: 'Zen-Whoberi',
    description: 'Deadliest woman in the galaxy. Daughter of Thanos. Guardian.',
    appearance: { ascii: '🗡️💚', color: '#22c55e', description: 'Green skin with sword' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Advanced', culture: 'Assassin', aggression: 'High' },
    dialogue: ['I\'m a warrior, an assassin. I don\'t dance.', 'I have lived most of my life surrounded by my enemies.', 'You\'re mortal. How?'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 2000
  },

  drax: {
    id: 'drax',
    name: 'Drax the Destroyer',
    origin: 'Marvel Comics',
    homeworld: 'Unknown',
    description: 'Literal-minded warrior. Seeks vengeance. Nothing goes over his head.',
    appearance: { ascii: '💪🔪', color: '#84cc16', description: 'Muscular with red tattoos' },
    traits: { temperament: 'Aggressive', intelligence: 'Average', technology: 'None', culture: 'Warrior', aggression: 'Very High' },
    dialogue: ['Nothing goes over my head. My reflexes are too fast.', 'I am going to die surrounded by the biggest idiots in the galaxy.', 'Why would I put my finger on his throat?'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1500
  },

  nebula: {
    id: 'nebula',
    name: 'Nebula',
    origin: 'Marvel Comics',
    homeworld: 'Luphom',
    description: 'Cyborg assassin. Daughter of Thanos. Reformed Guardian.',
    appearance: { ascii: '🤖💙', color: '#3b82f6', description: 'Blue cybernetic enhancements' },
    traits: { temperament: 'Neutral', intelligence: 'Very High', technology: 'Superior', culture: 'Assassin', aggression: 'High' },
    dialogue: ['You were the one who wanted to win. I just wanted a sister.', 'I wasn\'t always like this.', 'It was either him or a tree.'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 1900
  },

  korg: {
    id: 'korg',
    name: 'Korg',
    origin: 'Marvel Comics',
    homeworld: 'Ria',
    description: 'Kronan warrior. Made of rocks. Gentle revolutionary.',
    appearance: { ascii: '🪨😊', color: '#78716c', description: 'Rock-like humanoid' },
    traits: { temperament: 'Friendly', intelligence: 'Average', technology: 'None', culture: 'Revolutionary', aggression: 'Low' },
    dialogue: ['Piss off, ghost!', 'The only thing that makes sense is that nothing makes sense.', 'Hey man, I\'m Korg. This is Miek.'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1200
  },

  blackpanther: {
    id: 'blackpanther',
    name: 'Black Panther',
    origin: 'Marvel Comics',
    homeworld: 'Wakanda',
    description: 'King of Wakanda. T\'Challa. Protector empowered by the Heart-Shaped Herb.',
    appearance: { ascii: '🐾👑', color: '#9333ea', description: 'Black vibranium suit with claws' },
    traits: { temperament: 'Friendly', intelligence: 'Genius', technology: 'Superior', culture: 'Royalty', aggression: 'Medium' },
    dialogue: ['Wakanda forever!', 'In my culture, death is not the end.', 'I am not king of all people. I am king of Wakanda.'],
    tradeable: true, hostile: false, rarity: 'Legendary', tradeValue: 2500
  },

  shuri: {
    id: 'shuri',
    name: 'Shuri',
    origin: 'Marvel Comics',
    homeworld: 'Wakanda',
    description: 'Princess of Wakanda. Genius inventor. Designs vibranium technology.',
    appearance: { ascii: '🔬👸', color: '#9333ea', description: 'Wakandan royal attire with tech' },
    traits: { temperament: 'Friendly', intelligence: 'Genius', technology: 'Superior', culture: 'Scientist', aggression: 'Low' },
    dialogue: ['Just because something works doesn\'t mean it can\'t be improved.', 'Don\'t scare me like that, colonizer!', 'I\'ve already designed better armor.'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 2200
  },

  okoye: {
    id: 'okoye',
    name: 'Okoye',
    origin: 'Marvel Comics',
    homeworld: 'Wakanda',
    description: 'General of the Dora Milaje. Loyal warrior. Protector of Wakanda.',
    appearance: { ascii: '🗡️💪', color: '#dc2626', description: 'Red armor with spear' },
    traits: { temperament: 'Neutral', intelligence: 'High', technology: 'Advanced', culture: 'Warrior', aggression: 'High' },
    dialogue: ['Wakanda forever!', 'I am loyal to the throne.', 'Guns. So primitive.'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1600
  },

  captainmarvel: {
    id: 'captainmarvel',
    name: 'Captain Marvel',
    origin: 'Marvel Comics',
    homeworld: 'Earth-616',
    description: 'Carol Danvers. Most powerful Avenger. Binary powers from Tesseract.',
    appearance: { ascii: '⭐💫', color: '#fbbf24', description: 'Gold and blue suit with glowing fists' },
    traits: { temperament: 'Friendly', intelligence: 'Very High', technology: 'Superior', culture: 'Hero', aggression: 'High' },
    dialogue: ['Higher, further, faster, baby.', 'I\'ve been fighting with one hand tied behind my back.', 'I\'m coming to end it.'],
    tradeable: true, hostile: false, rarity: 'Legendary', tradeValue: 2800
  },

  ronan: {
    id: 'ronan',
    name: 'Ronan the Accuser',
    origin: 'Marvel Comics',
    homeworld: 'Hala',
    description: 'Kree fanatic. Wields the Universal Weapon. Seeks to destroy Xandar.',
    appearance: { ascii: '🔨⚫', color: '#1e293b', description: 'Black armor with hammer' },
    traits: { temperament: 'Hostile', intelligence: 'High', technology: 'Superior', culture: 'Fanatic', aggression: 'Very High' },
    dialogue: ['Xandar! You stand accused!', 'I will unfurl one thousand years of Kree justice!', 'The Kree will rise again!'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  yonrogg: {
    id: 'yonrogg',
    name: 'Yon-Rogg',
    origin: 'Marvel Comics',
    homeworld: 'Hala',
    description: 'Kree commander. Manipulated Carol Danvers. Starforce leader.',
    appearance: { ascii: '⚔️💚', color: '#22c55e', description: 'Green Kree military uniform' },
    traits: { temperament: 'Hostile', intelligence: 'Very High', technology: 'Superior', culture: 'Military', aggression: 'High' },
    dialogue: ['Control your impulses.', 'You\'re just a human.', 'The Supreme Intelligence will judge you.'],
    tradeable: false, hostile: true, rarity: 'Uncommon', tradeValue: 0
  },

  talos: {
    id: 'talos',
    name: 'Talos',
    origin: 'Marvel Comics',
    homeworld: 'Skrullos',
    description: 'Skrull general. Refugee seeking a home. Shapeshifter ally.',
    appearance: { ascii: '👽💚', color: '#22c55e', description: 'Green Skrull with pointed ears' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Advanced', culture: 'Refugee', aggression: 'Low' },
    dialogue: ['I\'m not gonna fight your war. I\'m gonna end it.', 'We\'re just fighting for a home.', 'Congratulations, Agent Fury.'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1400
  },

  skrull: {
    id: 'skrull',
    name: 'Skrull',
    origin: 'Marvel Comics',
    homeworld: 'Skrullos',
    description: 'Shapeshifting alien race. Refugees from Kree war. Can mimic anyone.',
    appearance: { ascii: '👽🔄', color: '#22c55e', description: 'Green reptilian humanoid' },
    traits: { temperament: 'Neutral', intelligence: 'High', technology: 'Advanced', culture: 'Shapeshifter', aggression: 'Medium' },
    dialogue: ['*Shapeshifts into your form*', 'We are not your enemy.', 'The Kree destroyed our home.'],
    tradeable: true, hostile: false, rarity: 'Common', tradeValue: 1100
  },

  ego: {
    id: 'ego',
    name: 'Ego the Living Planet',
    origin: 'Marvel Comics',
    homeworld: 'Ego',
    description: 'Celestial being. Living planet. Peter Quill\'s father.',
    appearance: { ascii: '🌍👤', color: '#3b82f6', description: 'Planetary consciousness' },
    traits: { temperament: 'Hostile', intelligence: 'Godlike', technology: 'Godlike', culture: 'Celestial', aggression: 'Very High' },
    dialogue: ['I am a Celestial!', 'I created what I imagined biological life to be like.', 'It broke my heart to put that tumor in her head.'],
    tradeable: false, hostile: true, rarity: 'Legendary', tradeValue: 0
  },

  mantis: {
    id: 'mantis',
    name: 'Mantis',
    origin: 'Marvel Comics',
    homeworld: 'Unknown',
    description: 'Empath. Can sense and manipulate emotions. Guardian of the Galaxy.',
    appearance: { ascii: '🦗💚', color: '#84cc16', description: 'Green skin with antennae' },
    traits: { temperament: 'Friendly', intelligence: 'Average', technology: 'None', culture: 'Empath', aggression: 'None' },
    dialogue: ['I can feel your feelings!', 'You are in love with Gamora!', 'Sleep!'],
    tradeable: true, hostile: false, rarity: 'Uncommon', tradeValue: 1300
  },

  redskull: {
    id: 'redskull',
    name: 'Red Skull',
    origin: 'Marvel Comics',
    homeworld: 'Earth-616',
    description: 'Nazi villain. Cursed to guard the Soul Stone on Vormir.',
    appearance: { ascii: '💀🔴', color: '#dc2626', description: 'Red skull face in black cloak' },
    traits: { temperament: 'Hostile', intelligence: 'Genius', technology: 'Advanced', culture: 'Villain', aggression: 'Very High' },
    dialogue: ['Welcome to Vormir.', 'A soul for a soul.', 'What you seek lies before you.'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  eitri: {
    id: 'eitri',
    name: 'Eitri',
    origin: 'Marvel Comics',
    homeworld: 'Nidavellir',
    description: 'King of the Dwarves. Master weaponsmith. Forged Mjolnir and Stormbreaker.',
    appearance: { ascii: '🔨👑', color: '#78716c', description: 'Giant dwarf with metal hands' },
    traits: { temperament: 'Friendly', intelligence: 'Genius', technology: 'Magic', culture: 'Craftsman', aggression: 'Low' },
    dialogue: ['You understand, boy, you\'re about to take the full force of a star.', 'That\'s a king\'s weapon.', 'Thanos came here and forced us to forge the gauntlet.'],
    tradeable: true, hostile: false, rarity: 'Rare', tradeValue: 2400
  },

  // ============================================================================
  // DC UNIVERSE
  // ============================================================================

  superman: {
    id: 'superman',
    name: 'Superman',
    origin: 'DC',
    homeworld: 'Krypton',
    description: 'Last Son of Krypton. Man of Steel. Truth, justice, and a better tomorrow.',
    appearance: { ascii: '🦸💙', color: '#0044cc', description: 'Blue suit with red cape and S symbol' },
    traits: { temperament: 'Friendly', intelligence: 'Very High', technology: 'Kryptonian', culture: 'Hero', aggression: 'Low' },
    dialogue: ['Truth, justice, and a better tomorrow.', 'I\'m here to help.', 'Up, up, and away!'],
    tradeable: false, hostile: false, rarity: 'Legendary', tradeValue: 0
  },

  batman: {
    id: 'batman',
    name: 'Batman',
    origin: 'DC',
    homeworld: 'Earth-Prime',
    description: 'The Dark Knight. World\'s Greatest Detective. "I\'m Batman."',
    appearance: { ascii: '🦇🌃', color: '#333333', description: 'Dark cape and cowl with bat symbol' },
    traits: { temperament: 'Neutral', intelligence: 'Genius', technology: 'Advanced', culture: 'Hero', aggression: 'Medium' },
    dialogue: ['I\'m Batman.', 'It\'s not who I am underneath, but what I do that defines me.', 'Justice, not vengeance.'],
    tradeable: false, hostile: false, rarity: 'Legendary', tradeValue: 0
  },

  wonderwoman: {
    id: 'wonderwoman',
    name: 'Wonder Woman',
    origin: 'DC',
    homeworld: 'Themyscira',
    description: 'Amazon warrior. Princess Diana. Lasso of Truth.',
    appearance: { ascii: '⚔️👸', color: '#ff0000', description: 'Amazon armor with golden lasso' },
    traits: { temperament: 'Friendly', intelligence: 'Very High', technology: 'Magic', culture: 'Amazon', aggression: 'Medium' },
    dialogue: ['I am Diana of Themyscira.', 'Only love can save the world.', 'For Themyscira!'],
    tradeable: false, hostile: false, rarity: 'Legendary', tradeValue: 0
  },

  flash: {
    id: 'flash',
    name: 'The Flash',
    origin: 'DC',
    homeworld: 'Earth-Prime',
    description: 'Fastest man alive. Speed Force conduit. Barry Allen.',
    appearance: { ascii: '⚡🏃', color: '#ff0000', description: 'Red suit with lightning bolt' },
    traits: { temperament: 'Friendly', intelligence: 'Very High', technology: 'Speed Force', culture: 'Hero', aggression: 'Low' },
    dialogue: ['My name is Barry Allen, and I am the fastest man alive.', 'Run, Barry, run!', '*whoosh*'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  greenlantern: {
    id: 'greenlantern',
    name: 'Green Lantern',
    origin: 'DC',
    homeworld: 'Earth-Prime',
    description: 'Wielder of the Green Lantern ring. Willpower incarnate. Hal Jordan.',
    appearance: { ascii: '💚💍', color: '#00ff00', description: 'Green and black uniform with ring' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Oan', culture: 'Hero', aggression: 'Medium' },
    dialogue: ['In brightest day, in blackest night...', 'Beware my power, Green Lantern\'s light!', 'Willpower is everything.'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  aquaman: {
    id: 'aquaman',
    name: 'Aquaman',
    origin: 'DC',
    homeworld: 'Atlantis',
    description: 'King of Atlantis. Commands the seas. Arthur Curry.',
    appearance: { ascii: '🔱🌊', color: '#0088ff', description: 'Orange and green armor with trident' },
    traits: { temperament: 'Friendly', intelligence: 'High', technology: 'Atlantean', culture: 'Atlantean', aggression: 'Medium' },
    dialogue: ['I am the protector of the deep.', 'The ocean does not forgive.', 'For Atlantis!'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  darkseid: {
    id: 'darkseid',
    name: 'Darkseid',
    origin: 'DC',
    homeworld: 'Apokolips',
    description: 'Lord of Apokolips. Seeks the Anti-Life Equation. Omega Beams.',
    appearance: { ascii: '👁️🔥', color: '#ff4400', description: 'Massive figure with glowing red eyes' },
    traits: { temperament: 'Hostile', intelligence: 'Godlike', technology: 'Superior', culture: 'Tyrant', aggression: 'Very High' },
    dialogue: ['Darkseid is.', 'I am many things, Kal-El, but here, I am God.', 'Anti-Life justifies my hate!'],
    tradeable: false, hostile: true, rarity: 'Legendary', tradeValue: 0
  },

  joker: {
    id: 'joker',
    name: 'Joker',
    origin: 'DC',
    homeworld: 'Earth-Prime',
    description: 'Clown Prince of Crime. Batman\'s nemesis. Agent of chaos.',
    appearance: { ascii: '🃏😈', color: '#9933ff', description: 'Purple suit with green hair and white face' },
    traits: { temperament: 'Hostile', intelligence: 'Genius', technology: 'None', culture: 'Chaos', aggression: 'Very High' },
    dialogue: ['Why so serious?', 'All it takes is one bad day.', '*maniacal laughter*'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  lexluthor: {
    id: 'lexluthor',
    name: 'Lex Luthor',
    origin: 'DC',
    homeworld: 'Earth-Prime',
    description: 'Genius billionaire. Superman\'s greatest enemy. Human superiority.',
    appearance: { ascii: '🧠💼', color: '#00ff00', description: 'Bald man in business suit' },
    traits: { temperament: 'Hostile', intelligence: 'Genius', technology: 'Superior', culture: 'Corporate', aggression: 'High' },
    dialogue: ['Some people can read War and Peace and come away thinking it\'s a simple adventure story.', 'Do you know what the problem is with being smarter than everyone else?', 'Superman must be stopped.'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  harleyquinn: {
    id: 'harleyquinn',
    name: 'Harley Quinn',
    origin: 'DC',
    homeworld: 'Earth-Prime',
    description: 'Former psychiatrist. Joker\'s girlfriend. Chaotic anti-hero.',
    appearance: { ascii: '🔨💖', color: '#ff00ff', description: 'Pigtails with red and black outfit' },
    traits: { temperament: 'Neutral', intelligence: 'High', technology: 'None', culture: 'Chaos', aggression: 'High' },
    dialogue: ['I\'m not a lady, I\'m a Harley!', 'Puddin\'!', 'Nice to meetcha! Love your perfume. What is that, the stench of death?'],
    tradeable: false, hostile: false, rarity: 'Rare', tradeValue: 0
  },

  sinestro: {
    id: 'sinestro',
    name: 'Sinestro',
    origin: 'DC',
    homeworld: 'Korugar',
    description: 'Former Green Lantern. Wields yellow ring of fear. Sinestro Corps leader.',
    appearance: { ascii: '💛😈', color: '#ffff00', description: 'Yellow and black uniform with ring' },
    traits: { temperament: 'Hostile', intelligence: 'Very High', technology: 'Oan', culture: 'Tyrant', aggression: 'Very High' },
    dialogue: ['In blackest day, in brightest night, beware your fears made into light.', 'Fear is the most powerful force in the universe.', 'Order through fear.'],
    tradeable: false, hostile: true, rarity: 'Rare', tradeValue: 0
  },

  brainiac: {
    id: 'brainiac',
    name: 'Brainiac',
    origin: 'DC',
    homeworld: 'Colu',
    description: 'Artificial intelligence. Collects cities. 12th-level intellect.',
    appearance: { ascii: '🤖🧠', color: '#00ff00', description: 'Green android with skull cap' },
    traits: { temperament: 'Hostile', intelligence: 'Godlike', technology: 'Superior', culture: 'AI', aggression: 'High' },
    dialogue: ['I am Brainiac. I am knowledge.', 'Your world will be preserved... in a bottle.', 'Resistance is illogical.'],
    tradeable: false, hostile: true, rarity: 'Legendary', tradeValue: 0
  },

  loislane: {
    id: 'loislane',
    name: 'Lois Lane',
    origin: 'DC Comics',
    homeworld: 'Earth-Prime',
    description: 'Intrepid Daily Planet reporter. Superman\'s love interest. Fearless investigative journalist.',
    appearance: {
      ascii: '📰👩',
      color: '#4169e1',
      description: 'Professional woman with press badge and determined expression'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Journalist',
      aggression: 'Low'
    },
    dialogue: [
      'I smell a story here.',
      'The truth will come out, it always does.',
      'Superman, is that you?',
      'This is Lois Lane, Daily Planet.',
      'I don\'t need saving, I need answers.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 800
  },

  catwoman: {
    id: 'catwoman',
    name: 'Catwoman',
    origin: 'DC Comics',
    homeworld: 'Earth-Prime',
    description: 'Master thief and anti-hero. Selina Kyle. Batman\'s complicated love interest.',
    appearance: {
      ascii: '🐱🖤',
      color: '#000000',
      description: 'Sleek black catsuit with cat-like grace and whip'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Thief',
      aggression: 'Medium'
    },
    dialogue: [
      'Meow. Miss me, Batman?',
      'I\'m not a hero. I\'m a thief.',
      'Nice kitty.',
      'Curiosity killed the cat, but satisfaction brought it back.',
      'I take what I want, when I want it.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1200
  },

  robin: {
    id: 'robin',
    name: 'Robin',
    origin: 'DC Comics',
    homeworld: 'Earth-Prime',
    description: 'Batman\'s sidekick and protégé. Various individuals have held the mantle.',
    appearance: {
      ascii: '🐦⚔️',
      color: '#ff0000',
      description: 'Red, yellow and green costume with cape and utility belt'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Hero',
      aggression: 'Medium'
    },
    dialogue: [
      'Holy smokes, Batman!',
      'I\'ve got your back, Batman.',
      'The Dynamic Duo strikes again!',
      'Justice never sleeps.',
      'I\'m not just a sidekick anymore.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Common',
    tradeValue: 900
  },

  hippolyta: {
    id: 'hippolyta',
    name: 'Queen Hippolyta',
    origin: 'DC Comics',
    homeworld: 'Themyscira',
    description: 'Queen of the Amazons. Wonder Woman\'s mother. Immortal warrior queen.',
    appearance: {
      ascii: '👑⚔️',
      color: '#ffd700',
      description: 'Regal Amazon queen in golden armor with crown'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Magic',
      culture: 'Amazon',
      aggression: 'Medium'
    },
    dialogue: [
      'I am Queen of the Amazons.',
      'My daughter will bring peace to Man\'s World.',
      'The gods have blessed Themyscira.',
      'Wisdom comes through battle and sacrifice.',
      'Protect the innocent, my daughter.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2000
  },

  amazon: {
    id: 'amazon',
    name: 'Amazon Warrior',
    origin: 'DC Comics',
    homeworld: 'Themyscira',
    description: 'Immortal warrior women from Paradise Island. Trained in combat and wisdom.',
    appearance: {
      ascii: '⚔️👩',
      color: '#b8860b',
      description: 'Armored warrior woman with spear and shield'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Magic',
      culture: 'Amazon',
      aggression: 'Medium'
    },
    dialogue: [
      'For Themyscira and the Queen!',
      'We are the daughters of the gods.',
      'Honor guides our blades.',
      'Man\'s World has much to learn.',
      'Sisters in arms, sisters in heart.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1500
  },

  parademon: {
    id: 'parademon',
    name: 'Parademon',
    origin: 'DC Comics',
    homeworld: 'Apokolips',
    description: 'Winged shock troops of Darkseid. Mindless servants of Apokolips.',
    appearance: {
      ascii: '👹🦇',
      color: '#8b0000',
      description: 'Demonic winged creature with claws and fangs'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Low',
      technology: 'Advanced',
      culture: 'Military',
      aggression: 'Very High'
    },
    dialogue: [
      '*Screeching battle cry*',
      'For Darkseid!',
      '*Aggressive snarling*',
      'Apokolips rises!',
      '*Mindless roaring*'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Common',
    tradeValue: 600
  },

  desaad: {
    id: 'desaad',
    name: 'Desaad',
    origin: 'DC Comics',
    homeworld: 'Apokolips',
    description: 'Darkseid\'s chief torturer and scientist. Master of pain and suffering.',
    appearance: {
      ascii: '🔬😈',
      color: '#4b0082',
      description: 'Gaunt figure in dark robes with cruel instruments'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'Apokolips',
      aggression: 'High'
    },
    dialogue: [
      'Pain is the greatest teacher.',
      'Your suffering pleases me.',
      'I serve the will of Darkseid.',
      'Science and torture are one.',
      'Scream for me, little one.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare',
    tradeValue: 1800
  },

  highfather: {
    id: 'highfather',
    name: 'Highfather',
    origin: 'DC Comics',
    homeworld: 'New Genesis',
    description: 'Leader of New Genesis. Benevolent ruler opposing Darkseid\'s tyranny.',
    appearance: {
      ascii: '👴✨',
      color: '#ffffff',
      description: 'Wise elderly figure in white robes with staff'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Godlike',
      technology: 'Superior',
      culture: 'New Genesis',
      aggression: 'Low'
    },
    dialogue: [
      'The Source guides us all.',
      'Peace through understanding.',
      'Light shall triumph over darkness.',
      'My children, be strong.',
      'The Anti-Life Equation must never be found.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Legendary',
    tradeValue: 3000
  },

  orion: {
    id: 'orion',
    name: 'Orion',
    origin: 'DC Comics',
    homeworld: 'New Genesis',
    description: 'Son of Darkseid raised on New Genesis. The Dog of War fighting his nature.',
    appearance: {
      ascii: '🐕⚡',
      color: '#ff4500',
      description: 'Powerful warrior with astro-harness and fierce expression'
    },
    traits: {
      temperament: 'Neutral',
      intelligence: 'High',
      technology: 'Superior',
      culture: 'New Genesis',
      aggression: 'High'
    },
    dialogue: [
      'I am Orion, son of Darkseid!',
      'The rage burns within me.',
      'For New Genesis!',
      'I fight the darkness in my blood.',
      'The Dog of War knows no mercy.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2200
  },

  mistermiracle: {
    id: 'mistermiracle',
    name: 'Mister Miracle',
    origin: 'DC Comics',
    homeworld: 'New Genesis',
    description: 'Master escape artist. Scott Free. Raised on Apokolips but fights for freedom.',
    appearance: {
      ascii: '🎭🔓',
      color: '#ff6347',
      description: 'Colorful costume with cape and escape artist gear'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Superior',
      culture: 'New Genesis',
      aggression: 'Low'
    },
    dialogue: [
      'No trap can hold Mister Miracle!',
      'Freedom is the greatest escape.',
      'The impossible is just another challenge.',
      'Darkseid is... but I am free.',
      'Every chain can be broken.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 2000
  },

  zod: {
    id: 'zod',
    name: 'General Zod',
    origin: 'DC Comics',
    homeworld: 'Krypton',
    description: 'Kryptonian military leader. Superman\'s enemy. "Kneel before Zod!"',
    appearance: {
      ascii: '👨‍✈️⚡',
      color: '#2f4f4f',
      description: 'Military uniform with Kryptonian insignia and commanding presence'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Kryptonian',
      culture: 'Military',
      aggression: 'Very High'
    },
    dialogue: [
      'Kneel before Zod!',
      'I am your superior in every way.',
      'Krypton will rise again!',
      'You will bow down before me, Jor-El!',
      'I was born to rule!'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Legendary',
    tradeValue: 2500
  },

  adamstrange: {
    id: 'adamstrange',
    name: 'Adam Strange',
    origin: 'DC Comics',
    homeworld: 'Rann',
    description: 'Earth archaeologist transported to planet Rann. Hero of two worlds.',
    appearance: {
      ascii: '🚀👨',
      color: '#ff1493',
      description: 'Red and white uniform with jet pack and ray gun'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Very High',
      technology: 'Advanced',
      culture: 'Hero',
      aggression: 'Medium'
    },
    dialogue: [
      'Science will save the day.',
      'Rann needs a hero.',
      'Two worlds, one destiny.',
      'The Zeta Beam brought me here.',
      'Alanna, I\'ll find a way back.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1400
  },

  sardath: {
    id: 'sardath',
    name: 'Sardath',
    origin: 'DC Comics',
    homeworld: 'Rann',
    description: 'Rannian scientist. Creator of the Zeta Beam. Adam Strange\'s father-in-law.',
    appearance: {
      ascii: '🔬👽',
      color: '#9370db',
      description: 'Alien scientist with advanced technology and wise demeanor'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'Genius',
      technology: 'Superior',
      culture: 'Scientific',
      aggression: 'Very Low'
    },
    dialogue: [
      'The Zeta Beam is my greatest creation.',
      'Science transcends worlds.',
      'Welcome to Rann, Adam Strange.',
      'Knowledge is the greatest weapon.',
      'My daughter chose well.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 1800
  },

  hawkman: {
    id: 'hawkman',
    name: 'Hawkman',
    origin: 'DC Comics',
    homeworld: 'Thanagar',
    description: 'Thanagarian police officer. Reincarnated Egyptian prince. Wings and mace.',
    appearance: {
      ascii: '🦅⚔️',
      color: '#daa520',
      description: 'Winged warrior with hawk helmet and ancient weapons'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Thanagarian',
      aggression: 'Medium'
    },
    dialogue: [
      'By the wings of Horus!',
      'Justice soars on swift wings.',
      'I remember lives across the centuries.',
      'Thanagar\'s honor guides me.',
      'Hawkgirl and I are eternal.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1600
  },

  hawkgirl: {
    id: 'hawkgirl',
    name: 'Hawkgirl',
    origin: 'DC Comics',
    homeworld: 'Thanagar',
    description: 'Thanagarian warrior. Hawkman\'s eternal partner. Fierce and loyal.',
    appearance: {
      ascii: '🦅👩',
      color: '#daa520',
      description: 'Winged female warrior with hawk helmet and mace'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Thanagarian',
      aggression: 'Medium'
    },
    dialogue: [
      'Thanagar\'s daughter fights for justice.',
      'My wings carry me to battle.',
      'Carter and I are bound by destiny.',
      'The mace of my ancestors strikes true.',
      'Love transcends death itself.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Uncommon',
    tradeValue: 1600
  },

  mera: {
    id: 'mera',
    name: 'Mera',
    origin: 'DC Comics',
    homeworld: 'Atlantis',
    description: 'Queen of Atlantis. Aquaman\'s wife. Hydrokinetic powers.',
    appearance: {
      ascii: '🌊👸',
      color: '#00ced1',
      description: 'Atlantean queen with flowing red hair and water powers'
    },
    traits: {
      temperament: 'Friendly',
      intelligence: 'High',
      technology: 'Atlantean',
      culture: 'Atlantean',
      aggression: 'Medium'
    },
    dialogue: [
      'The seas obey my command.',
      'Atlantis will not be threatened.',
      'Arthur, the ocean calls to us.',
      'Water is life, and I am its master.',
      'The depths hold ancient power.'
    ],
    tradeable: true,
    hostile: false,
    rarity: 'Rare',
    tradeValue: 1900
  },

  oceanmaster: {
    id: 'oceanmaster',
    name: 'Ocean Master',
    origin: 'DC Comics',
    homeworld: 'Atlantis',
    description: 'Orm Marius. Aquaman\'s half-brother. Would-be ruler of Atlantis.',
    appearance: {
      ascii: '🌊😈',
      color: '#4682b4',
      description: 'Atlantean in dark armor with trident and crown'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Atlantean',
      culture: 'Atlantean',
      aggression: 'High'
    },
    dialogue: [
      'I am the true king of Atlantis!',
      'The surface world will bow to the sea.',
      'Brother, you chose the wrong side.',
      'The ocean\'s fury is mine to command.',
      'Atlantis deserves a pure-blood ruler.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare',
    tradeValue: 1700
  },

  reverseflash: {
    id: 'reverseflash',
    name: 'Reverse-Flash',
    origin: 'DC Comics',
    homeworld: 'Earth-Prime',
    description: 'Eobard Thawne. Flash\'s greatest enemy. Speedster from the future.',
    appearance: {
      ascii: '⚡😈',
      color: '#ffff00',
      description: 'Yellow and red speedster suit with reverse lightning'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'Very High',
      technology: 'Speed Force',
      culture: 'Villain',
      aggression: 'Very High'
    },
    dialogue: [
      'I am the Reverse-Flash!',
      'It was me, Barry!',
      'I am your greatest enemy.',
      'The timeline is mine to control.',
      'You cannot outrun your destiny.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Rare',
    tradeValue: 2100
  },

  captaincold: {
    id: 'captaincold',
    name: 'Captain Cold',
    origin: 'DC Comics',
    homeworld: 'Earth-Prime',
    description: 'Leonard Snart. Leader of the Rogues. Wields absolute zero cold gun.',
    appearance: {
      ascii: '❄️🔫',
      color: '#87ceeb',
      description: 'Blue parka with cold gun and icy demeanor'
    },
    traits: {
      temperament: 'Hostile',
      intelligence: 'High',
      technology: 'Advanced',
      culture: 'Criminal',
      aggression: 'High'
    },
    dialogue: [
      'The name\'s Captain Cold.',
      'Ice to meet you.',
      'The Rogues have rules.',
      'Absolute zero, Flash.',
      'Central City is my turf.'
    ],
    tradeable: false,
    hostile: true,
    rarity: 'Uncommon',
    tradeValue: 1300
  }
};

/**
 * Get alien species by ID
 */
export const getAlienSpecies = (speciesId) => {
  return AlienSpecies[speciesId] || null;
};

/**
 * Get all alien species
 */
export const getAllAlienSpecies = () => {
  return Object.values(AlienSpecies);
};

/**
 * Get alien species by origin universe
 */
export const getAliensByOrigin = (origin) => {
  return Object.values(AlienSpecies).filter(species => species.origin === origin);
};

/**
 * Get tradeable alien species
 */
export const getTradeableAliens = () => {
  return Object.values(AlienSpecies).filter(species => species.tradeable);
};

/**
 * Get hostile alien species
 */
export const getHostileAliens = () => {
  return Object.values(AlienSpecies).filter(species => species.hostile);
};

/**
 * Get aliens by rarity
 */
export const getAliensByRarity = (rarity) => {
  return Object.values(AlienSpecies).filter(species => species.rarity === rarity);
};

/**
 * Get random dialogue from alien species
 */
export const getRandomDialogue = (speciesId) => {
  const species = getAlienSpecies(speciesId);
  if (!species || !species.dialogue || species.dialogue.length === 0) {
    return 'Hello, traveler.';
  }
  return species.dialogue[Math.floor(Math.random() * species.dialogue.length)];
};

export default AlienSpecies;
